import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Boxes,
    Brush,
    Circle,
    Flame,
    FlipHorizontal2,
    Frame,
    Grid3x3,
    Hexagon,
    Image as ImageIcon,
    Link2,
    Magnet,
    Maximize2,
    Minus,
    MousePointer2,
    Paintbrush,
    Paintbrush2,
    PenLine,
    Plus,
    RotateCw,
    Sparkles,
    Spline,
    Square,
    SquareDashed,
    Waypoints,
} from "lucide-react";
import { useSvgDiagram2D } from "./SvgDiagram2DContext";
import {
    clientToSvgPoint,
    ZOOM_STEP,
    MIN_ZOOM,
    MAX_ZOOM,
} from "../../hooks/useSvgViewBoxZoom";
import {
    nodeShapeGeometry,
    radialEmitterSegments,
    concentricRingRadii,
    groupOutlinePath,
    resolveGroupOutline,
    bridgedConnectionChains,
    chainConnectionPaths,
    connectionObjectPlacements,
    connectionObjectTransform,
    parseConnectionObject,
    recolorObjectForHeat,
    applyHeatColor,
    DEFAULT_HEAT_ZONE_COLOR,
    ellipsePath,
    linePath,
    diagramArcPath,
    rectPath,
    rectCenter,
    polygonPath,
    presetStrokeShapePaths,
    presetStrokeObjectPlacements,
    ellipseOutline,
    rectOutline,
    polygonOutline,
    lineOutline,
    arcOutline,
    mirrorPositions,
    mirrorPointSets,
    findNodeNearPosition,
    findShapeNearPoints,
    resolveFillPaint,
    patternDefSvg,
    scalePointAbout,
    rotatePointAbout,
    mirroredTwoPointUpdates,
} from "../../utils/svgDiagram2DModel";
import type {
    DiagramArc,
    DiagramEllipse,
    DiagramHeatZone,
    DiagramLine,
    DiagramNode,
    DiagramPattern,
    DiagramPolygon,
    DiagramRect,
    DiagramTool,
    Fill,
    GroupToolMode,
    NodeGroup,
    NodeInteractAction,
    OutlineProperty,
    ShapeOutline,
    StrokeStyle,
    SymmetryState,
    Vec2,
} from "../../utils/svgDiagram2DModel";
import { SvgDiagram2DNodePanel } from "./SvgDiagram2DNodePanel";
import { SvgDiagram2DEllipsePanel } from "./SvgDiagram2DEllipsePanel";
import { SvgDiagram2DLinePanel } from "./SvgDiagram2DLinePanel";
import { SvgDiagram2DArcPanel } from "./SvgDiagram2DArcPanel";
import { SvgDiagram2DRectPanel } from "./SvgDiagram2DRectPanel";
import { SvgDiagram2DPolygonPanel } from "./SvgDiagram2DPolygonPanel";
import { SvgDiagram2DMultiSelectPanel } from "./SvgDiagram2DMultiSelectPanel";
import { SvgDiagram2DMultiNodePanel } from "./SvgDiagram2DMultiNodePanel";
import { SvgDiagram2DSubtoolPanel } from "./SvgDiagram2DSubtoolPanel";
import { SvgDiagram2DContextMenu } from "./SvgDiagram2DContextMenu";
import type {
    CanvasContextMenuState,
    CanvasContextTarget,
    CanvasTargetKind,
} from "./SvgDiagram2DContextMenu";
import { ToolRail } from "../reusable/ToolRail";
import type { ToolRailItem } from "../reusable/ToolRail";
import { ToolRailPopover } from "../reusable/ToolRailPopover";
import type { ToolRailPopoverItem } from "../reusable/ToolRailPopover";
import {
    CanvasSection,
    ConnectionPresetsSection,
    EffectsSection,
    GroupOutlinePresetsSection,
    PatternLibrarySection,
    ReferenceImageSection,
    StrokeStylesSection,
    SymmetrySection,
} from "./SvgDiagram2DSectionPanels";

const SUBTOOL_ITEMS: ReadonlyArray<ToolRailItem<DiagramTool>> = [
    { id: "select", label: "Select", Icon: MousePointer2, hotkey: "V" },
    { id: "place-node", label: "Place Node", Icon: Plus, hotkey: "N" },
    { id: "node-brush", label: "Node brush", Icon: Brush, hotkey: "D" },
    {
        id: "multi-node-brush",
        label: "Multi-node brush",
        Icon: Paintbrush,
        hotkey: "F",
    },
    {
        id: "mega-node-brush",
        label: "Mega-node brush",
        Icon: Paintbrush2,
        hotkey: "J",
    },
    { id: "interact", label: "Attract / repel / connect", Icon: Magnet, hotkey: "I" },
    { id: "ellipse", label: "Ellipse", Icon: Circle, hotkey: "E" },
    { id: "line", label: "Line", Icon: Minus, hotkey: "L" },
    { id: "arc", label: "Arc", Icon: Spline, hotkey: "A" },
    { id: "rect", label: "Rectangle", Icon: Square, hotkey: "B" },
    { id: "polygon", label: "Polygon", Icon: Hexagon, hotkey: "P" },
    { id: "heat-zone", label: "Heat zone", Icon: Flame, hotkey: "H" },
    { id: "group", label: "Group", Icon: Boxes, hotkey: "G" },
    { id: "connect", label: "Connect", Icon: Link2, hotkey: "K" },
    {
        id: "connect-selection",
        label: "Connect selection",
        Icon: Waypoints,
    },
    { id: "scale", label: "Scale", Icon: Maximize2, hotkey: "S" },
    { id: "rotate", label: "Rotate", Icon: RotateCw, hotkey: "R" },
];

/**
 * The config "library" sections shown on the canvas's right rail, each opening
 * in a popover. These were previously collapsible groups in the control panel;
 * the panel now keeps only the element lists, selection actions, and handoff.
 */
const CONFIG_RAIL_ITEMS: ReadonlyArray<ToolRailPopoverItem> = [
    { id: "canvas", label: "Canvas", Icon: Frame, render: () => <CanvasSection /> },
    {
        id: "reference-image",
        label: "Reference image",
        Icon: ImageIcon,
        render: () => <ReferenceImageSection />,
    },
    {
        id: "symmetry",
        label: "Symmetry",
        Icon: FlipHorizontal2,
        render: () => <SymmetrySection />,
    },
    {
        id: "stroke-styles",
        label: "Stroke styles",
        Icon: PenLine,
        render: () => <StrokeStylesSection />,
    },
    {
        id: "connection-presets",
        label: "Connection presets",
        Icon: Spline,
        render: () => <ConnectionPresetsSection />,
    },
    {
        id: "effects",
        label: "Effects",
        Icon: Sparkles,
        render: () => <EffectsSection />,
    },
    {
        id: "pattern-fills",
        label: "Pattern fills",
        Icon: Grid3x3,
        render: () => <PatternLibrarySection />,
    },
    {
        id: "group-outline-presets",
        label: "Group outline presets",
        Icon: SquareDashed,
        render: () => <GroupOutlinePresetsSection />,
    },
];

const SELECTED_COLOR = "#ffd166";
const CONNECT_SOURCE_COLOR = "#06d6a0";
const SCALE_ANCHOR_COLOR = "#ff6b6b";
const ROTATE_PIVOT_COLOR = "#ff6b6b";
/** Influence-ring color for the interact subtool's cursor preview, per action. */
const INTERACT_PREVIEW_COLOR: Record<NodeInteractAction, string> = {
    attract: "#ffa94d",
    repel: "#4dabf7",
    connect: "#06d6a0",
};
/** Figma-style magenta alignment guide shown while a node snaps during a drag. */
const SNAP_GUIDE_COLOR = "#ff3d8b";
/** Editor-only center line guides toggled from the Symmetry panel. */
const CENTER_GUIDE_COLOR = "#3da5ff";
const MOVE_THRESHOLD = 3;
/** Min pointer-to-anchor distance (SVG units) before the scale tool engages,
 * so a press right on the anchor doesn't divide by ~0. */
const SCALE_MIN_START_DIST = 4;
/** Min per-axis start offset (SVG units) for that axis to be scaled; below it
 * the press was effectively aligned with the anchor on that axis, so dividing
 * by it would be unstable and the axis is left at factor 1. */
const SCALE_MIN_AXIS_DIST = 1;
/** Min pointer-to-pivot distance (SVG units) before the rotate tool engages, so
 * a press right on the pivot yields a stable starting angle. */
const ROTATE_MIN_START_DIST = 4;
/** Min length (SVG units) a line/arc drag must span to commit (else it's a
 * click, ignored — mirrors the ellipse tool's min-size guard). */
const TWO_POINT_MIN_LENGTH = 2;

/**
 * The element kinds the right-click context menu handles, paired with the
 * `data-*-id` attribute each carries on the canvas. The menu resolution walks up
 * from the clicked node to the nearest ancestor with one of these attributes.
 */
const CONTEXT_TARGET_ATTRS: ReadonlyArray<
    [attr: string, kind: CanvasTargetKind]
> = [
    ["data-node-id", "node"],
    ["data-ellipse-id", "ellipse"],
    ["data-line-id", "line"],
    ["data-arc-id", "arc"],
    ["data-rect-id", "rect"],
    ["data-polygon-id", "polygon"],
];

/** The visible region of the canvas, in document coordinates. */
interface ViewBox {
    x: number;
    y: number;
    w: number;
    h: number;
}

/**
 * A two-point shape (line/arc/rect) mover: both endpoints at drag start.
 * `mirrors` (when set) names the existing mirror-copy shape for each active
 * mirror of `mirrorPointSets` (in that order, "" when none was found), captured
 * by geometry at drag start so a drag under symmetry keeps each counterpart a
 * true reflection — the shape analogue of how nodes carry `mirrorIds`.
 */
interface TwoPointMover {
    id: string;
    start: Vec2;
    end: Vec2;
    mirrors?: string[];
}

/**
 * A polygon mover: every vertex at drag start (translation shifts them all).
 * `mirrors` carries the symmetric counterpart polygon ids the same way
 * `TwoPointMover.mirrors` does.
 */
interface PolygonMover {
    id: string;
    points: Vec2[];
    mirrors?: string[];
}

/**
 * An ellipse mover: its center at drag start. `mirrors` carries the symmetric
 * counterpart ellipse ids (matched by center, in `mirrorPositions` order) so a
 * drag under symmetry moves each counterpart's center to the reflected point.
 */
interface EllipseMover {
    id: string;
    start: Vec2;
    mirrors?: string[];
}

/**
 * An in-progress single-point (vertex / endpoint / corner) drag on a selected
 * line, arc, rect, or polygon. Unlike a whole-shape drag this moves one point of
 * the shape while the rest stay put. `point` identifies which point: line/arc
 * endpoints are `"start"`/`"end"`, rect corners are one of four `"corner-*"`
 * ids, and polygon vertices are the vertex index. `startGeom` snapshots the
 * shape's defining geometry at press time so the move is computed from a stable
 * base (and the corner that stays pinned for rects reads from it).
 */
type ShapePointKind = "line" | "arc" | "rect" | "polygon" | "heat-zone";

type RectCorner = "corner-tl" | "corner-tr" | "corner-br" | "corner-bl";

interface PointDragState {
    kind: ShapePointKind;
    id: string;
    /** Which point of the shape is being dragged. */
    point: "start" | "end" | RectCorner | number;
    /** Drag-start pointer position (SVG space), for computing the move delta. */
    startPoint: Vec2;
    /** Shape geometry captured at press time (the move is applied to this). */
    startGeom: { start?: Vec2; end?: Vec2; points?: Vec2[] };
    /**
     * The shape's rotation (degrees) at press time, for a rotated rect whose
     * corner handles are shown spun into place. The pointer delta is un-rotated
     * by this before it's applied to the axis-aligned box. 0 for everything else.
     */
    rotation: number;
    /**
     * For a heat-zone corner drag under active symmetry: the existing mirror-copy
     * zone ids, paired by `mirrorPointSets([start, end])` order ("" when none was
     * found). Each frame the dragged box is re-mirrored so its counterparts stay
     * true reflections, matching how whole-shape drags carry their `mirrors`.
     */
    mirrors?: string[];
    /** Whether the pointer has moved past the click threshold. */
    moved: boolean;
    downX: number;
    downY: number;
}

interface DragState {
    /** Node currently being dragged (select tool only); null otherwise. */
    nodeId: string | null;
    /** Ellipse currently being dragged (select tool only); null otherwise. */
    ellipseId: string | null;
    /** Line currently being dragged (select tool only); null otherwise. */
    lineId: string | null;
    /** Arc currently being dragged (select tool only); null otherwise. */
    arcId: string | null;
    /** Rect currently being dragged (select tool only); null otherwise. */
    rectId: string | null;
    /** Polygon currently being dragged (select tool only); null otherwise. */
    polygonId: string | null;
    downX: number;
    downY: number;
    moved: boolean;
    /** Set when this drag spawned a duplicate (alt-drag), so the click-up
     * handler doesn't override the duplicate's selection. */
    duplicated: boolean;
    /** Drag-start point in SVG space (for computing the move delta). */
    startPoint: Vec2 | null;
    /**
     * Nodes moved by this drag, with their positions at drag start. Each mover
     * also carries `mirrorIds`: its symmetric counterpart node ids (in
     * `mirrorPositions` order), pinned here at drag start so the drag keeps
     * driving the same counterparts even as the moved node sweeps past other
     * symmetric nodes. Empty when symmetry is off or no counterpart exists.
     */
    movers: Array<{ id: string; start: Vec2; mirrorIds?: (string | null)[] }>;
    /** Ellipses moved by this drag (mixed selections drag both). */
    ellipseMovers: EllipseMover[];
    /** Lines moved by this drag (mixed selections drag all shapes). */
    lineMovers: TwoPointMover[];
    /** Arcs moved by this drag (mixed selections drag all shapes). */
    arcMovers: TwoPointMover[];
    /** Rects moved by this drag (mixed selections drag all shapes). */
    rectMovers: TwoPointMover[];
    /** Polygons moved by this drag (mixed selections drag all shapes). */
    polygonMovers: PolygonMover[];
}

interface RectBounds {
    x: number;
    y: number;
    w: number;
    h: number;
}

/** A fresh, idle drag state (no grabbed shape, no movers). */
function emptyDragState(): DragState {
    return {
        nodeId: null,
        ellipseId: null,
        lineId: null,
        arcId: null,
        rectId: null,
        polygonId: null,
        downX: 0,
        downY: 0,
        moved: false,
        duplicated: false,
        startPoint: null,
        movers: [],
        ellipseMovers: [],
        lineMovers: [],
        arcMovers: [],
        rectMovers: [],
        polygonMovers: [],
    };
}

function rectFromPoints(a: Vec2, b: Vec2): RectBounds {
    return {
        x: Math.min(a.x, b.x),
        y: Math.min(a.y, b.y),
        w: Math.abs(a.x - b.x),
        h: Math.abs(a.y - b.y),
    };
}

/**
 * The four corners of an axis-aligned rect (defined by opposite corners
 * `start`/`end`), keyed by visual position regardless of which way the box was
 * dragged out. Point-editing drags a corner and rebuilds the box from the
 * dragged corner plus its (pinned) opposite, so the editor keys corners by
 * their on-screen position (top-left/…) rather than by start/end.
 */
function rectCorners(start: Vec2, end: Vec2): Record<RectCorner, Vec2> {
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);
    return {
        "corner-tl": { x: minX, y: minY },
        "corner-tr": { x: maxX, y: minY },
        "corner-br": { x: maxX, y: maxY },
        "corner-bl": { x: minX, y: maxY },
    };
}

/** The corner diagonally opposite `corner` (stays pinned while `corner` drags). */
function oppositeRectCorner(corner: RectCorner): RectCorner {
    switch (corner) {
        case "corner-tl":
            return "corner-br";
        case "corner-tr":
            return "corner-bl";
        case "corner-br":
            return "corner-tl";
        case "corner-bl":
            return "corner-tr";
    }
}

/** Per-axis median of a non-empty list of points (the scale tool's anchor). */
function medianPoint(points: Vec2[]): Vec2 {
    const median = (vals: number[]): number => {
        const sorted = [...vals].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 === 0
            ? (sorted[mid - 1]! + sorted[mid]!) / 2
            : sorted[mid]!;
    };
    return {
        x: median(points.map((p) => p.x)),
        y: median(points.map((p) => p.y)),
    };
}

/** Rotate point `p` about `center` by `deg` degrees clockwise (SVG +y is down). */
function rotateAround(p: Vec2, center: Vec2, deg: number): Vec2 {
    const rad = (deg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const dx = p.x - center.x;
    const dy = p.y - center.y;
    return {
        x: center.x + dx * cos - dy * sin,
        y: center.y + dx * sin + dy * cos,
    };
}

/**
 * Snap a drag delta to the nearest of 8 directions — horizontal, vertical, or
 * the two 45° diagonals — by picking whichever axis the pointer is closest to
 * and zeroing/equalizing the other component. Used while shift is held so a
 * drag is locked to a straight line.
 */
function constrainDeltaToAxes(dx: number, dy: number): Vec2 {
    const ax = Math.abs(dx);
    const ay = Math.abs(dy);
    // Within ~22.5° of an axis the diagonal component is less than tan(22.5°)
    // of the dominant one; outside that band, snap to the diagonal.
    const diagThreshold = Math.tan(Math.PI / 8); // ≈ 0.4142
    if (ay <= ax * diagThreshold) return { x: dx, y: 0 }; // horizontal
    if (ax <= ay * diagThreshold) return { x: 0, y: dy }; // vertical
    const mag = (ax + ay) / 2; // diagonal: equal magnitude on both axes
    return { x: Math.sign(dx) * mag, y: Math.sign(dy) * mag };
}

/** Snap distance in screen pixels; divided by zoom to get the canvas-unit
 * threshold, so the "stickiness" feels constant regardless of zoom level. */
const SNAP_SCREEN_PX = 8;

/** Candidate snap line on one axis, plus where the matching guide should span. */
interface SnapLine {
    /** The coordinate (x for vertical lines, y for horizontal). */
    coord: number;
    /** Perpendicular extent of the guide, for drawing only. */
    min: number;
    max: number;
}

/** A guide line to render while a snap is active. */
export interface SnapGuide {
    axis: "x" | "y";
    coord: number;
    min: number;
    max: number;
}

/**
 * Result of snapping a drag delta: the (possibly nudged) delta plus any guide
 * lines to draw for the axes that snapped.
 */
interface SnapResult {
    dx: number;
    dy: number;
    guides: SnapGuide[];
}

/**
 * Adjust a drag delta so the moving points align to nearby static guides.
 * X and Y snap independently: for each axis we take the moving points' bounding
 * edges and center, find the nearest candidate line within `threshold`, and
 * nudge the whole delta by the smallest correction that lands an edge/center on
 * it. `xLines`/`yLines` are the static candidates (other nodes' coords, canvas
 * center, canvas edges). Returns guides for whichever axes snapped.
 */
function snapDelta(
    movingPoints: Vec2[],
    dx: number,
    dy: number,
    xLines: SnapLine[],
    yLines: SnapLine[],
    threshold: number,
): SnapResult {
    const guides: SnapGuide[] = [];

    // Snap one axis. `proposed` is the list of candidate moving coordinates on
    // that axis after the raw delta (min edge, center, max edge); we pick the
    // (movingCoord, line) pair with the smallest gap and shift by that gap.
    const snapAxis = (
        proposed: number[],
        lines: SnapLine[],
    ): { correction: number; line: SnapLine } | null => {
        let best: { correction: number; line: SnapLine } | null = null;
        for (const p of proposed) {
            for (const line of lines) {
                const gap = line.coord - p;
                if (Math.abs(gap) > threshold) continue;
                if (!best || Math.abs(gap) < Math.abs(best.correction)) {
                    best = { correction: gap, line };
                }
            }
        }
        return best;
    };

    if (movingPoints.length > 0 && xLines.length > 0) {
        const xs = movingPoints.map((p) => p.x + dx);
        const proposed = [Math.min(...xs), Math.max(...xs)];
        // Center too, so a single point (min === max) still snaps once.
        proposed.push((proposed[0]! + proposed[1]!) / 2);
        const hit = snapAxis(proposed, xLines);
        if (hit) {
            dx += hit.correction;
            guides.push({
                axis: "x",
                coord: hit.line.coord,
                min: hit.line.min,
                max: hit.line.max,
            });
        }
    }

    if (movingPoints.length > 0 && yLines.length > 0) {
        const ys = movingPoints.map((p) => p.y + dy);
        const proposed = [Math.min(...ys), Math.max(...ys)];
        proposed.push((proposed[0]! + proposed[1]!) / 2);
        const hit = snapAxis(proposed, yLines);
        if (hit) {
            dy += hit.correction;
            guides.push({
                axis: "y",
                coord: hit.line.coord,
                min: hit.line.min,
                max: hit.line.max,
            });
        }
    }

    return { dx, dy, guides };
}

/**
 * Render a standalone shape's outline styled by a stroke style: the style's
 * parallel stroked lines and/or objects-along-path, populated on the shape's own
 * `outline`. Mirrors the exporter's `presetStrokeShapeEls` so the live canvas
 * matches the exported SVG. `fill` paints the first (innermost) stroke path so a
 * filled shape keeps its interior; `seedKey` (the shape id) seeds random object
 * order. Everything is `pointerEvents: none` — the shape's own transparent
 * hit-area path (rendered alongside) handles interaction.
 */
function PresetStrokeShape({
    outline,
    style,
    fill,
    seedKey,
    opacity,
    heatZones = [],
}: {
    outline: ShapeOutline;
    style: StrokeStyle;
    fill: string;
    seedKey: string;
    opacity: number;
    heatZones?: ReadonlyArray<DiagramHeatZone>;
}) {
    if (outline.points.length < 2) return null;
    const kind = style.renderKind ?? "stroke";
    const objectsAvailable =
        (style.objectSvgs?.length ?? 0) > 0 &&
        (style.objectSpacing ?? 0) > 0;
    const drawStroke =
        kind === "stroke" || kind === "both" || !objectsAvailable;
    const els: React.ReactNode[] = [];
    if (drawStroke) {
        presetStrokeShapePaths(outline.points, outline.closed, style).forEach(
            (d, i) => {
                els.push(
                    <path
                        key={`s-${i}`}
                        d={d}
                        // Only the first line carries the fill (the interior);
                        // the rest are stroke-only.
                        fill={i === 0 ? fill : "none"}
                        stroke={style.color ?? "#a0c4ff"}
                        strokeWidth={style.strokeWidth ?? 1.5}
                        strokeDasharray={style.dash || undefined}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />,
                );
            },
        );
    }
    if ((kind === "objects" || kind === "both") && objectsAvailable) {
        const parsed = style.objectSvgs.map((o) => parseConnectionObject(o.svg));
        presetStrokeObjectPlacements(
            outline.points,
            outline.closed,
            style,
            seedKey,
        ).forEach((p, i) => {
            const obj = parsed[p.objectIndex];
            if (!obj) return;
            els.push(
                <g
                    key={`o-${i}`}
                    transform={connectionObjectTransform(p, obj, style)}
                    dangerouslySetInnerHTML={{
                        __html: recolorObjectForHeat(
                            obj.inner,
                            style,
                            { x: p.x, y: p.y },
                            heatZones,
                        ),
                    }}
                />,
            );
        });
    }
    return (
        <g style={{ opacity, pointerEvents: "none" }}>{els}</g>
    );
}

export function SvgDiagram2DViewer() {
    const {
        doc,
        tool,
        setTool,
        selectedNodeIds,
        toggleNodeSelection,
        selectInRect,
        groupNodesFromMarquee,
        clearSelection,
        connectSourceId,
        viewBoxWidth,
        viewBoxHeight,
        background,
        snapEnabled,
        refImage,
        refImageVisible,
        refImageOpacity,
        canExport,
        handleDownload,
        addNodeAt,
        nodeBrushOptions,
        applyNodeBrush,
        multiNodeBrushOptions,
        applyMultiNodeBrush,
        megaNodeBrushOptions,
        applyMegaNodeBrush,
        nodeInteractOptions,
        applyNodeInteraction,
        moveNodes,
        findNodeMirrorIds,
        handleNodePick,
        insertNodeOnConnection,
        setSelectedNodeId,
        setNodePanelOpen,
        duplicateNode,
        pushSnapshot,
        selectedEllipseIds,
        setSelectedEllipseId,
        toggleEllipseSelection,
        addEllipse,
        moveEllipses,
        updateEllipse,
        duplicateEllipse,
        selectedLineIds,
        setSelectedLineId,
        toggleLineSelection,
        addLine,
        moveLine,
        moveLines,
        duplicateLine,
        selectedArcIds,
        setSelectedArcId,
        toggleArcSelection,
        addArc,
        moveArc,
        moveArcs,
        duplicateArc,
        selectedRectIds,
        setSelectedRectId,
        toggleRectSelection,
        addRect,
        updateRect,
        moveRect,
        moveRects,
        duplicateRect,
        selectedPolygonIds,
        setSelectedPolygonId,
        togglePolygonSelection,
        addPolygon,
        movePolygon,
        movePolygons,
        duplicatePolygon,
        selectedHeatZoneIds,
        setSelectedHeatZoneId,
        toggleHeatZoneSelection,
        addHeatZone,
        moveHeatZones,
        hoveredId,
        setHoveredId,
        configSectionRevealRequest,
    } = useSvgDiagram2D();

    /**
     * Cross-highlight handlers for a canvas element: hovering it marks it as the
     * hovered id (so its panel row lights up) and clears on leave. Reused by
     * every shape group and the nodes. Returns props spread onto the element.
     */
    const hoverProps = useCallback(
        (id: string) => ({
            onPointerEnter: () => setHoveredId(id),
            onPointerLeave: () =>
                setHoveredId((cur) => (cur === id ? null : cur)),
        }),
        [setHoveredId],
    );

    const svgRef = useRef<SVGSVGElement>(null);
    // Mirror of the document for use inside stable drag callbacks (so snapping
    // can read the current node/ellipse positions without re-creating handlers).
    const docRef = useRef(doc);
    docRef.current = doc;

    // Layer gating: hidden layers' elements don't render or take pointer events;
    // locked layers' elements render normally but can't be grabbed/selected.
    const hiddenLayerIds = useMemo(
        () =>
            new Set(doc.layers.filter((l) => !l.visible).map((l) => l.id)),
        [doc.layers],
    );
    const lockedLayerIds = useMemo(
        () => new Set(doc.layers.filter((l) => l.locked).map((l) => l.id)),
        [doc.layers],
    );
    const layerHidden = useCallback(
        (layerId: string) => hiddenLayerIds.has(layerId),
        [hiddenLayerIds],
    );
    const layerLocked = useCallback(
        (layerId: string) => lockedLayerIds.has(layerId),
        [lockedLayerIds],
    );
    // Canvas dimensions, mirrored for the snap callback (center/edge guides).
    const canvasDimsRef = useRef({ w: viewBoxWidth, h: viewBoxHeight });
    canvasDimsRef.current = { w: viewBoxWidth, h: viewBoxHeight };

    // Pattern fills: a lookup for resolving fills to `url(#…)` paints, and the
    // `<pattern>` defs injected into the canvas SVG so those references resolve
    // (the same defs `renderDiagram2DToSvg` emits on export).
    const patternById = useMemo(
        () => new Map<string, DiagramPattern>(doc.patterns.map((p) => [p.id, p])),
        [doc.patterns],
    );
    const resolveFill = useCallback(
        (fill: Fill | null): string => resolveFillPaint(fill, patternById),
        [patternById],
    );
    const patternDefsMarkup = useMemo(
        () => doc.patterns.map((p) => patternDefSvg(p)).join(""),
        [doc.patterns],
    );

    const dragRef = useRef<DragState>(emptyDragState());
    /**
     * In-progress single-point drag (a vertex / endpoint / corner handle on the
     * one selected line, arc, rect, or polygon). Null when no handle is grabbed.
     * Distinct from `dragRef`, which moves whole shapes.
     */
    const pointDragRef = useRef<PointDragState | null>(null);
    /** Start point (SVG space) of an in-progress rectangle selection. */
    const marqueeStartRef = useRef<Vec2 | null>(null);
    const [marqueeRect, setMarqueeRect] = useState<RectBounds | null>(null);
    /**
     * For the Group subtool: the operation the in-progress marquee will perform,
     * pinned from the modifier held at drag start (null = the marquee belongs to
     * the plain Select subtool, which selects rather than groups).
     */
    const groupModeRef = useRef<GroupToolMode | null>(null);

    /**
     * In-progress ellipse creation (ellipse subtool): `start` is the press
     * point in SVG space; the drag defines the ellipse's bounding box. While a
     * drag is live the preview rect/ellipse is shown, and on release an ellipse
     * is committed (unless the drag was too small).
     */
    const ellipseDrawStartRef = useRef<Vec2 | null>(null);
    const [ellipseDrawRect, setEllipseDrawRect] = useState<RectBounds | null>(
        null,
    );

    /**
     * In-progress line/arc creation (line & arc subtools): `start` is the press
     * point in SVG space; the drag's current point is the other endpoint. The
     * active subtool decides whether a line or an arc is committed on release
     * (both are drawn the same start→end way). A preview is shown while live.
     */
    const twoPointDrawStartRef = useRef<Vec2 | null>(null);
    const [twoPointDrawEnd, setTwoPointDrawEnd] = useState<Vec2 | null>(null);

    /**
     * In-progress polygon creation (polygon subtool). Unlike the drag-to-create
     * shapes, a polygon is built one vertex per click: each click on the canvas
     * appends a vertex, a rubber-band segment follows the cursor to the next,
     * and the shape is committed by clicking the first vertex again, double-
     * clicking, or pressing Enter. Esc cancels; Backspace drops the last vertex.
     * `polygonDraft` holds the placed vertices; `polygonCursor` is the live
     * cursor position for the preview.
     */
    const [polygonDraft, setPolygonDraft] = useState<Vec2[]>([]);
    const polygonDraftRef = useRef<Vec2[]>(polygonDraft);
    polygonDraftRef.current = polygonDraft;
    const [polygonCursor, setPolygonCursor] = useState<Vec2 | null>(null);
    /** Screen-space radius for "click the first vertex to close" hit-testing. */
    const POLYGON_CLOSE_PX = 12;

    /**
     * Live cursor position (in SVG units) for the Attract/repel/connect subtool's
     * influence-radius preview — a ring that follows the pointer over the canvas
     * showing exactly which nodes a click would affect. Null when the pointer is
     * off the canvas or another subtool is active.
     */
    const [interactCursor, setInteractCursor] = useState<Vec2 | null>(null);

    /**
     * In-progress scale drag (scale subtool). Captured on pointer-down:
     * `anchor` is the median of every selected element's defining points,
     * `startDx`/`startDy` are the signed pointer-to-anchor offsets at press
     * time, and the mover arrays hold each scaled element's id and start
     * geometry. Ellipses also record their start radii so the radii grow with
     * the factor. Like Illustrator, X and Y scale independently from the
     * pointer's per-axis travel (factorX = currentDx/startDx, factorY =
     * currentDy/startDy); holding Shift forces a uniform factor on both axes.
     */
    const scaleRef = useRef<{
        anchor: Vec2;
        startDx: number;
        startDy: number;
        movers: Array<{ id: string; start: Vec2 }>;
        ellipseMovers: Array<{ id: string; start: Vec2; rx: number; ry: number }>;
        lineMovers: Array<{ id: string; start: Vec2; end: Vec2 }>;
        arcMovers: Array<{ id: string; start: Vec2; end: Vec2 }>;
        rectMovers: Array<{ id: string; start: Vec2; end: Vec2 }>;
        polygonMovers: Array<{ id: string; points: Vec2[] }>;
    } | null>(null);
    /** Live anchor marker shown while the scale tool is engaged. */
    const [scaleAnchor, setScaleAnchor] = useState<Vec2 | null>(null);

    /**
     * In-progress rotate drag (rotate subtool). Captured on pointer-down:
     * `pivot` is the median of every selected element's defining points,
     * `startAngle` is the pointer-to-pivot angle at press time, and the mover
     * arrays hold each rotated element's id and start geometry. Ellipses also
     * record their start `startAngle` so a partial-arc carve spins with the
     * rotation. Rotation delta = currentAngle - startAngle.
     *
     * Under active symmetry, non-node movers also carry `mirrors`: the existing
     * mirror-copy shapes found by geometry at press time, one entry per distinct
     * mirror of `mirrorPointSets` (in the same order). Each frame the mover's
     * source geometry is rotated, then re-mirrored so every counterpart stays a
     * true reflection — the shape analogue of how `moveNodes` drags node
     * counterparts. Nodes need no `mirrors` here because `moveNodes` already
     * handles their symmetry.
     */
    const rotateRef = useRef<{
        pivot: Vec2;
        startAngle: number;
        /** Symmetry active at press time, used to re-mirror rotated copies. */
        symmetry: SymmetryState;
        movers: Array<{ id: string; start: Vec2 }>;
        ellipseMovers: Array<{
            id: string;
            start: Vec2;
            startAngleDeg: number;
            /** The ellipse body's rotation (deg) at press time; the drag spins it. */
            startRotationDeg: number;
            mirrors: Array<{
                id: string;
                startAngleDeg: number;
                startRotationDeg: number;
                /** Whether this mirror reflects handedness (odd # of axes). */
                flipped: boolean;
            }>;
        }>;
        lineMovers: Array<{
            id: string;
            start: Vec2;
            end: Vec2;
            mirrors: string[];
        }>;
        arcMovers: Array<{
            id: string;
            start: Vec2;
            end: Vec2;
            mirrors: string[];
        }>;
        rectMovers: Array<{
            id: string;
            start: Vec2;
            end: Vec2;
            /** The box body's rotation (deg) at press time; the drag spins it. */
            startRotationDeg: number;
            /** Mirror copies with each one's start rotation + handedness flip. */
            mirrors: Array<{
                id: string;
                startRotationDeg: number;
                flipped: boolean;
            }>;
        }>;
        polygonMovers: Array<{
            id: string;
            points: Vec2[];
            mirrors: string[];
        }>;
    } | null>(null);
    /** Live pivot marker shown while the rotate tool is engaged. */
    const [rotateAnchor, setRotateAnchor] = useState<Vec2 | null>(null);

    /**
     * Connection currently hovered while both its endpoints are selected — the
     * one showing the "insert node" button at its midpoint. Null when none.
     */
    const [hoveredConnectionId, setHoveredConnectionId] = useState<
        string | null
    >(null);

    /**
     * The open right-click context menu (its cursor position + the element it
     * targets), or null when no menu is open. See `handleContextMenu`.
     */
    const [contextMenu, setContextMenu] =
        useState<CanvasContextMenuState | null>(null);

    /** Alignment guide lines shown while a snap is active during a drag. */
    const [snapGuides, setSnapGuides] = useState<SnapGuide[]>([]);
    // Mirror of snapEnabled for use inside the stable drag-move callback.
    const snapEnabledRef = useRef(snapEnabled);
    snapEnabledRef.current = snapEnabled;
    // Tracks whether guides are currently shown, so the move handler only calls
    // setSnapGuides([]) once when a snap releases (not every pointer move).
    const snapGuidesActiveRef = useRef(false);
    snapGuidesActiveRef.current = snapGuides.length > 0;

    // View transform (zoom/pan). View-only: it changes the rendered viewBox but
    // never the document's export dimensions (viewBoxWidth/viewBoxHeight).
    const [view, setView] = useState<ViewBox>({
        x: 0,
        y: 0,
        w: viewBoxWidth,
        h: viewBoxHeight,
    });
    // Mirror of `view` for use inside stable pointer-down callbacks.
    const viewRef = useRef(view);
    viewRef.current = view;
    // Keep the view in sync when the document dimensions change, unless the user
    // has already zoomed/panned away from the default frame.
    const viewedDimsRef = useRef({ w: viewBoxWidth, h: viewBoxHeight });
    useEffect(() => {
        const prev = viewedDimsRef.current;
        viewedDimsRef.current = { w: viewBoxWidth, h: viewBoxHeight };
        setView((cur) => {
            const isDefaultFrame =
                cur.x === 0 &&
                cur.y === 0 &&
                cur.w === prev.w &&
                cur.h === prev.h;
            return isDefaultFrame
                ? { x: 0, y: 0, w: viewBoxWidth, h: viewBoxHeight }
                : cur;
        });
    }, [viewBoxWidth, viewBoxHeight]);

    /** Middle-button pan: start point in client coords + view at press time. */
    const panRef = useRef<{
        startX: number;
        startY: number;
        view: ViewBox;
    } | null>(null);

    const resetView = useCallback(() => {
        setView({ x: 0, y: 0, w: viewBoxWidth, h: viewBoxHeight });
    }, [viewBoxWidth, viewBoxHeight]);

    // --- Polygon drawing (multi-click) ------------------------------------

    /** Drop the in-progress polygon draft without committing it. */
    const cancelPolygonDraft = useCallback(() => {
        setPolygonDraft([]);
        setPolygonCursor(null);
    }, []);

    /**
     * Commit the in-progress polygon if it has at least 3 vertices, then clear
     * the draft. Fewer than 3 vertices can't form a closed shape, so a stray
     * click or double-click is discarded.
     */
    const commitPolygonDraft = useCallback(() => {
        // Drop consecutive (near-)duplicate vertices — a double-click close
        // places the final vertex twice — so the committed shape has no
        // degenerate zero-length edges.
        const pts: Vec2[] = [];
        for (const p of polygonDraftRef.current) {
            const prev = pts[pts.length - 1];
            if (prev && Math.hypot(p.x - prev.x, p.y - prev.y) < 0.5) continue;
            pts.push(p);
        }
        if (pts.length >= 3) addPolygon(pts);
        cancelPolygonDraft();
    }, [addPolygon, cancelPolygonDraft]);

    /**
     * Handle a polygon-subtool click at SVG point `pt`. Clicking on (or very
     * near) the first vertex closes the shape; any other click appends a vertex.
     * `zoom` converts the screen close-radius into canvas units.
     */
    const handlePolygonClick = useCallback(
        (pt: Vec2, zoom: number) => {
            const pts = polygonDraftRef.current;
            if (pts.length >= 3) {
                const first = pts[0]!;
                const closeDist = POLYGON_CLOSE_PX / zoom;
                if (Math.hypot(pt.x - first.x, pt.y - first.y) <= closeDist) {
                    commitPolygonDraft();
                    return;
                }
            }
            setPolygonDraft([...pts, pt]);
            setPolygonCursor(pt);
        },
        [commitPolygonDraft],
    );

    // Keyboard while a polygon draft is in progress: Enter closes it, Escape
    // cancels, Backspace removes the last placed vertex. Only mounted during a
    // draft so it never intercepts keys at any other time.
    useEffect(() => {
        if (polygonDraft.length === 0) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (
                e.key !== "Enter" &&
                e.key !== "Escape" &&
                e.key !== "Backspace"
            ) {
                return;
            }
            // Run in the capture phase and stop here so the controls' global
            // Delete/Backspace handler doesn't also fire (Backspace would
            // otherwise delete a previously selected shape mid-draft).
            e.preventDefault();
            e.stopImmediatePropagation();
            if (e.key === "Enter") {
                commitPolygonDraft();
            } else if (e.key === "Escape") {
                cancelPolygonDraft();
            } else {
                setPolygonDraft((pts) => pts.slice(0, -1));
            }
        };
        window.addEventListener("keydown", onKeyDown, true);
        return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [polygonDraft.length, commitPolygonDraft, cancelPolygonDraft]);

    // Leaving the polygon subtool abandons any in-progress draft so a half-drawn
    // shape doesn't linger when the user switches tools.
    useEffect(() => {
        if (tool !== "polygon" && polygonDraftRef.current.length > 0) {
            cancelPolygonDraft();
        }
    }, [tool, cancelPolygonDraft]);

    // Leaving the interact subtool drops its influence-radius preview ring so it
    // doesn't linger on the canvas under another tool.
    useEffect(() => {
        if (tool !== "interact") setInteractCursor(null);
    }, [tool]);

    // Double-click on the canvas closes an in-progress polygon. The two presses
    // already placed a duplicate last vertex; commit drops nothing (the close
    // just finalizes the existing draft).
    const handleBgDoubleClick = useCallback(() => {
        if (tool === "polygon" && polygonDraftRef.current.length >= 3) {
            commitPolygonDraft();
        }
    }, [tool, commitPolygonDraft]);

    // --- Right-click context menu -----------------------------------------

    /** True when `id` is already part of the current selection for `kind`. */
    const isTargetSelected = useCallback(
        (target: CanvasContextTarget): boolean => {
            switch (target.kind) {
                case "node":
                    return selectedNodeIds.includes(target.id);
                case "ellipse":
                    return selectedEllipseIds.includes(target.id);
                case "line":
                    return selectedLineIds.includes(target.id);
                case "arc":
                    return selectedArcIds.includes(target.id);
                case "rect":
                    return selectedRectIds.includes(target.id);
                case "polygon":
                    return selectedPolygonIds.includes(target.id);
            }
        },
        [
            selectedNodeIds,
            selectedEllipseIds,
            selectedLineIds,
            selectedArcIds,
            selectedRectIds,
            selectedPolygonIds,
        ],
    );

    /** Replace the selection with just `target` (used before opening the menu). */
    const selectOnlyTarget = useCallback(
        (target: CanvasContextTarget) => {
            switch (target.kind) {
                case "node":
                    setSelectedNodeId(target.id);
                    break;
                case "ellipse":
                    setSelectedEllipseId(target.id);
                    break;
                case "line":
                    setSelectedLineId(target.id);
                    break;
                case "arc":
                    setSelectedArcId(target.id);
                    break;
                case "rect":
                    setSelectedRectId(target.id);
                    break;
                case "polygon":
                    setSelectedPolygonId(target.id);
                    break;
            }
        },
        [
            setSelectedNodeId,
            setSelectedEllipseId,
            setSelectedLineId,
            setSelectedArcId,
            setSelectedRectId,
            setSelectedPolygonId,
        ],
    );

    /**
     * Open the right-click menu for whichever element the press landed on. The
     * menu only applies to the select tool (other subtools use right-click for
     * their own flows, e.g. canceling a polygon draft). The target is resolved
     * from the nearest ancestor carrying a `data-*-id` attribute; a right-click
     * on empty canvas (or a locked-layer element, which carries no live data
     * attr through pointer events) opens nothing. If the target isn't already in
     * the selection, it becomes the sole selection first so the menu's actions
     * operate on it.
     */
    const handleContextMenu = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            if (tool !== "select") return;
            const el = e.target as Element | null;
            let resolved: CanvasContextTarget | null = null;
            for (const [attr, kind] of CONTEXT_TARGET_ATTRS) {
                const hit = el?.closest(`[${attr}]`);
                const id = hit?.getAttribute(attr);
                if (id) {
                    resolved = { kind, id };
                    break;
                }
            }
            if (!resolved) {
                setContextMenu(null);
                return;
            }
            if (!isTargetSelected(resolved)) selectOnlyTarget(resolved);
            setContextMenu({ x: e.clientX, y: e.clientY, target: resolved });
        },
        [tool, isTargetSelected, selectOnlyTarget],
    );

    const closeContextMenu = useCallback(() => setContextMenu(null), []);

    // Close the menu whenever the active subtool changes (its actions assume the
    // select tool, and a tool switch can change what's grabbable).
    useEffect(() => {
        setContextMenu(null);
    }, [tool]);

    // Wheel-to-zoom, anchored on the cursor. Registered as a native
    // non-passive listener so preventDefault() actually stops page scroll
    // (React 18 attaches onWheel passively at the root).
    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            const cursor = clientToSvgPoint(svg, e.clientX, e.clientY);
            setView((cur) => {
                const factor = Math.pow(ZOOM_STEP, e.deltaY);
                const baseScale = viewBoxWidth / cur.w; // current zoom level
                const nextScale = Math.min(
                    MAX_ZOOM,
                    Math.max(MIN_ZOOM, baseScale / factor),
                );
                const w = viewBoxWidth / nextScale;
                const h = viewBoxHeight / nextScale;
                // Keep `cursor` at the same fractional offset in the viewBox.
                const fx = (cursor.x - cur.x) / cur.w;
                const fy = (cursor.y - cur.y) / cur.h;
                return { x: cursor.x - fx * w, y: cursor.y - fy * h, w, h };
            });
        };
        svg.addEventListener("wheel", onWheel, { passive: false });
        return () => svg.removeEventListener("wheel", onWheel);
    }, [viewBoxWidth, viewBoxHeight]);

    const handleBgPointerDown = useCallback(
        (e: React.PointerEvent) => {
            // Middle button pans the view regardless of the active subtool.
            if (e.button === 1) {
                e.preventDefault();
                panRef.current = {
                    startX: e.clientX,
                    startY: e.clientY,
                    view: viewRef.current,
                };
                (e.currentTarget as Element).setPointerCapture(e.pointerId);
                return;
            }
            // The polygon subtool builds its shape from discrete clicks rather
            // than a drag, so handle the press here and skip the drag/marquee
            // machinery entirely. Left-click drops (or closes on) a vertex;
            // right-click cancels the in-progress draft.
            if (tool === "polygon") {
                const svg = svgRef.current;
                if (!svg) return;
                if (e.button === 2) {
                    cancelPolygonDraft();
                    return;
                }
                if (e.button !== 0) return;
                const pt = clientToSvgPoint(svg, e.clientX, e.clientY);
                const zoom = viewBoxWidth / viewRef.current.w;
                handlePolygonClick(pt, zoom);
                return;
            }
            dragRef.current = {
                ...emptyDragState(),
                downX: e.clientX,
                downY: e.clientY,
            };
            if (
                tool === "select" ||
                tool === "group" ||
                tool === "connect-selection"
            ) {
                const svg = svgRef.current;
                if (svg) {
                    const pt = clientToSvgPoint(svg, e.clientX, e.clientY);
                    marqueeStartRef.current = pt;
                    setMarqueeRect({ x: pt.x, y: pt.y, w: 0, h: 0 });
                    // The Group subtool's operation is chosen by the modifier
                    // held when the drag begins (Shift = add, Alt = remove,
                    // none = create); pin it now so releasing the key mid-drag
                    // doesn't change the action.
                    groupModeRef.current =
                        tool === "group"
                            ? e.shiftKey
                                ? "add"
                                : e.altKey
                                  ? "remove"
                                  : "create"
                            : null;
                }
            } else if (tool === "ellipse") {
                const svg = svgRef.current;
                if (svg) {
                    const pt = clientToSvgPoint(svg, e.clientX, e.clientY);
                    ellipseDrawStartRef.current = pt;
                    setEllipseDrawRect({ x: pt.x, y: pt.y, w: 0, h: 0 });
                    (e.currentTarget as Element).setPointerCapture(e.pointerId);
                }
            } else if (
                tool === "line" ||
                tool === "arc" ||
                tool === "rect" ||
                tool === "heat-zone"
            ) {
                const svg = svgRef.current;
                if (svg) {
                    const pt = clientToSvgPoint(svg, e.clientX, e.clientY);
                    twoPointDrawStartRef.current = pt;
                    setTwoPointDrawEnd(pt);
                    (e.currentTarget as Element).setPointerCapture(e.pointerId);
                }
            } else if (tool === "scale") {
                const svg = svgRef.current;
                // Spawn clones are derived from their host, so they can't be
                // scaled directly.
                const movers = doc.nodes
                    .filter(
                        (n) =>
                            selectedNodeIds.includes(n.id) && !n.spawnedFrom,
                    )
                    .map((n) => ({ id: n.id, start: { ...n.position } }));
                const ellipseMovers = doc.ellipses
                    .filter((el) => selectedEllipseIds.includes(el.id))
                    .map((el) => ({
                        id: el.id,
                        start: { ...el.center },
                        rx: el.rx,
                        ry: el.ry,
                    }));
                const lineMovers = doc.lines
                    .filter((l) => selectedLineIds.includes(l.id))
                    .map((l) => ({
                        id: l.id,
                        start: { ...l.start },
                        end: { ...l.end },
                    }));
                const arcMovers = doc.arcs
                    .filter((a) => selectedArcIds.includes(a.id))
                    .map((a) => ({
                        id: a.id,
                        start: { ...a.start },
                        end: { ...a.end },
                    }));
                const rectMovers = doc.rects
                    .filter((r) => selectedRectIds.includes(r.id))
                    .map((r) => ({
                        id: r.id,
                        start: { ...r.start },
                        end: { ...r.end },
                    }));
                const polygonMovers = doc.polygons
                    .filter((p) => selectedPolygonIds.includes(p.id))
                    .map((p) => ({
                        id: p.id,
                        points: p.points.map((pt) => ({ ...pt })),
                    }));
                // Every point that contributes to the spread / anchor: node
                // positions, ellipse centers, line/arc/rect endpoints, and
                // polygon vertices.
                const points: Vec2[] = [
                    ...movers.map((m) => m.start),
                    ...ellipseMovers.map((m) => m.start),
                    ...lineMovers.flatMap((m) => [m.start, m.end]),
                    ...arcMovers.flatMap((m) => [m.start, m.end]),
                    ...rectMovers.flatMap((m) => [m.start, m.end]),
                    ...polygonMovers.flatMap((m) => m.points),
                ];
                // Need a meaningful spread: 2+ distinct points across the
                // selection, or a single ellipse (whose radii can still grow).
                const canScale =
                    points.length >= 2 || ellipseMovers.length >= 1;
                if (svg && canScale) {
                    const anchor = medianPoint(points);
                    const pt = clientToSvgPoint(svg, e.clientX, e.clientY);
                    const startDx = pt.x - anchor.x;
                    const startDy = pt.y - anchor.y;
                    const startDist = Math.hypot(startDx, startDy);
                    // Drag must start away from the anchor so the initial ratios
                    // (currentD?/startD?) are well-defined.
                    if (startDist >= SCALE_MIN_START_DIST) {
                        pushSnapshot();
                        scaleRef.current = {
                            anchor,
                            startDx,
                            startDy,
                            movers,
                            ellipseMovers,
                            lineMovers,
                            arcMovers,
                            rectMovers,
                            polygonMovers,
                        };
                        setScaleAnchor(anchor);
                        (e.currentTarget as Element).setPointerCapture(
                            e.pointerId,
                        );
                    }
                }
            } else if (tool === "rotate") {
                const svg = svgRef.current;
                // Under active symmetry the rotate drag also turns each shape's
                // existing mirror copies so they stay true reflections. We match
                // counterparts by geometry at press time (never created), one per
                // distinct mirror of `mirrorPointSets`, in that order. A shared
                // `claimed` set per shape type keeps a selected original or an
                // already-paired copy from being grabbed twice.
                const sym = doc.symmetry;
                const { w, h } = canvasDimsRef.current;
                const center = { x: w / 2, y: h / 2 };
                /**
                 * The mirror-copy ids for a shape whose defining `points` are
                 * given, paired by `mirrorPointSets` order. `pool` is searched
                 * (excluding `claimed`); matched ids are added to `claimed`.
                 */
                const findMirrorIds = <T extends { id: string }>(
                    points: Vec2[],
                    pool: T[],
                    getPoints: (s: T) => Vec2[],
                    claimed: Set<string>,
                ): string[] =>
                    mirrorPointSets(points, sym, center).map((set) => {
                        const match = findShapeNearPoints(
                            pool,
                            set.points,
                            getPoints,
                            undefined,
                            claimed,
                        );
                        if (!match) return "";
                        claimed.add(match.id);
                        return match.id;
                    });

                // Spawn clones are derived from their host, so they can't be
                // rotated directly.
                const movers = doc.nodes
                    .filter(
                        (n) =>
                            selectedNodeIds.includes(n.id) && !n.spawnedFrom,
                    )
                    .map((n) => ({ id: n.id, start: { ...n.position } }));
                const ellipseClaimed = new Set(selectedEllipseIds);
                const ellipseMovers = doc.ellipses
                    .filter((el) => selectedEllipseIds.includes(el.id))
                    .map((el) => ({
                        id: el.id,
                        start: { ...el.center },
                        startAngleDeg: el.startAngle,
                        startRotationDeg: el.rotation,
                        // Ellipse geometry is matched by center alone; each copy
                        // keeps its own start-angle / body rotation so it spins
                        // from there. `flipped` (odd # of axes) negates the body
                        // rotation so a reflected copy turns the opposite way.
                        mirrors: mirrorPointSets([el.center], sym, center)
                            .map(({ points, flipped }) => {
                                const match = findShapeNearPoints(
                                    doc.ellipses,
                                    [points[0]!],
                                    (e2) => [e2.center],
                                    undefined,
                                    ellipseClaimed,
                                );
                                if (!match) return null;
                                ellipseClaimed.add(match.id);
                                return {
                                    id: match.id,
                                    startAngleDeg: match.startAngle,
                                    startRotationDeg: match.rotation,
                                    flipped,
                                };
                            })
                            .filter(
                                (
                                    m,
                                ): m is {
                                    id: string;
                                    startAngleDeg: number;
                                    startRotationDeg: number;
                                    flipped: boolean;
                                } => m !== null,
                            ),
                    }));
                const lineClaimed = new Set(selectedLineIds);
                const lineMovers = doc.lines
                    .filter((l) => selectedLineIds.includes(l.id))
                    .map((l) => ({
                        id: l.id,
                        start: { ...l.start },
                        end: { ...l.end },
                        mirrors: findMirrorIds(
                            [l.start, l.end],
                            doc.lines,
                            (s) => [s.start, s.end],
                            lineClaimed,
                        ),
                    }));
                const arcClaimed = new Set(selectedArcIds);
                const arcMovers = doc.arcs
                    .filter((a) => selectedArcIds.includes(a.id))
                    .map((a) => ({
                        id: a.id,
                        start: { ...a.start },
                        end: { ...a.end },
                        mirrors: findMirrorIds(
                            [a.start, a.end],
                            doc.arcs,
                            (s) => [s.start, s.end],
                            arcClaimed,
                        ),
                    }));
                const rectClaimed = new Set(selectedRectIds);
                const rectMovers = doc.rects
                    .filter((r) => selectedRectIds.includes(r.id))
                    .map((r) => ({
                        id: r.id,
                        start: { ...r.start },
                        end: { ...r.end },
                        startRotationDeg: r.rotation,
                        // Each mirror copy keeps its own start rotation; `flipped`
                        // (odd # of axes) negates it so a reflected box spins the
                        // opposite way (true mirror image of the turn).
                        mirrors: mirrorPointSets([r.start, r.end], sym, center)
                            .map(({ points, flipped }) => {
                                const match = findShapeNearPoints(
                                    doc.rects,
                                    points,
                                    (s) => [s.start, s.end],
                                    undefined,
                                    rectClaimed,
                                );
                                if (!match) return null;
                                rectClaimed.add(match.id);
                                return {
                                    id: match.id,
                                    startRotationDeg: match.rotation,
                                    flipped,
                                };
                            })
                            .filter(
                                (
                                    m,
                                ): m is {
                                    id: string;
                                    startRotationDeg: number;
                                    flipped: boolean;
                                } => m !== null,
                            ),
                    }));
                const polygonClaimed = new Set(selectedPolygonIds);
                const polygonMovers = doc.polygons
                    .filter((p) => selectedPolygonIds.includes(p.id))
                    .map((p) => ({
                        id: p.id,
                        points: p.points.map((pt) => ({ ...pt })),
                        mirrors: findMirrorIds(
                            p.points,
                            doc.polygons,
                            (s) => s.points,
                            polygonClaimed,
                        ),
                    }));
                // Every point that contributes to the pivot: node positions,
                // ellipse centers, line/arc/rect endpoints, polygon vertices.
                const points: Vec2[] = [
                    ...movers.map((m) => m.start),
                    ...ellipseMovers.map((m) => m.start),
                    ...lineMovers.flatMap((m) => [m.start, m.end]),
                    ...arcMovers.flatMap((m) => [m.start, m.end]),
                    ...rectMovers.flatMap((m) => [m.start, m.end]),
                    ...polygonMovers.flatMap((m) => m.points),
                ];
                // Need something to rotate: 2+ defining points so there is a
                // real offset to spin (a single point sits on the pivot and
                // wouldn't move), or a single ellipse that still visibly turns —
                // a non-circular body (rx ≠ ry), an existing tilt, or a partial-
                // arc carve. (A plain circle alone has nothing to show turning.)
                const ellipseSpins = ellipseMovers.some((m) => {
                    const el = doc.ellipses.find((e2) => e2.id === m.id);
                    return (
                        m.startAngleDeg !== 0 ||
                        m.startRotationDeg !== 0 ||
                        (el ? Math.abs(el.rx - el.ry) > 1e-6 : false)
                    );
                });
                const canRotate = points.length >= 2 || ellipseSpins;
                if (svg && canRotate) {
                    const pivot = medianPoint(points);
                    const pt = clientToSvgPoint(svg, e.clientX, e.clientY);
                    const startDist = Math.hypot(
                        pt.x - pivot.x,
                        pt.y - pivot.y,
                    );
                    // Drag must start away from the pivot so the initial angle is
                    // well-defined.
                    if (startDist >= ROTATE_MIN_START_DIST) {
                        pushSnapshot();
                        rotateRef.current = {
                            pivot,
                            startAngle: Math.atan2(
                                pt.y - pivot.y,
                                pt.x - pivot.x,
                            ),
                            symmetry: sym,
                            movers,
                            ellipseMovers,
                            lineMovers,
                            arcMovers,
                            rectMovers,
                            polygonMovers,
                        };
                        setRotateAnchor(pivot);
                        (e.currentTarget as Element).setPointerCapture(
                            e.pointerId,
                        );
                    }
                }
            }
        },
        [
            tool,
            doc.nodes,
            doc.ellipses,
            doc.lines,
            doc.arcs,
            doc.rects,
            doc.polygons,
            doc.symmetry,
            selectedNodeIds,
            selectedEllipseIds,
            selectedLineIds,
            selectedArcIds,
            selectedRectIds,
            selectedPolygonIds,
            pushSnapshot,
            viewBoxWidth,
            handlePolygonClick,
            cancelPolygonDraft,
        ],
    );

    const handleBgPointerMove = useCallback((e: React.PointerEvent) => {
        const pan = panRef.current;
        if (pan) {
            const svg = svgRef.current;
            if (!svg) return;
            const rect = svg.getBoundingClientRect();
            // Convert the client-pixel drag into document units.
            const scaleX = pan.view.w / rect.width;
            const scaleY = pan.view.h / rect.height;
            const dx = (e.clientX - pan.startX) * scaleX;
            const dy = (e.clientY - pan.startY) * scaleY;
            setView({
                x: pan.view.x - dx,
                y: pan.view.y - dy,
                w: pan.view.w,
                h: pan.view.h,
            });
            return;
        }
        // Attract/repel/connect subtool: track the cursor so the influence-radius
        // ring follows the pointer. No drag interaction runs from the background
        // under this subtool (it's dispatched on nodes), so just record the point.
        if (tool === "interact") {
            const svg = svgRef.current;
            if (svg) {
                setInteractCursor(clientToSvgPoint(svg, e.clientX, e.clientY));
            }
            return;
        }
        // Polygon draft: track the cursor so the rubber-band segment from the
        // last placed vertex follows the pointer. Only active mid-draft (which
        // only happens under the polygon subtool), so no tool check is needed.
        if (polygonDraftRef.current.length > 0) {
            const svg = svgRef.current;
            if (!svg) return;
            setPolygonCursor(clientToSvgPoint(svg, e.clientX, e.clientY));
            return;
        }
        const scale = scaleRef.current;
        if (scale) {
            const svg = svgRef.current;
            if (!svg) return;
            const pt = clientToSvgPoint(svg, e.clientX, e.clientY);
            const { anchor } = scale;
            const dx = pt.x - anchor.x;
            const dy = pt.y - anchor.y;
            // Illustrator-style: X and Y scale independently from the pointer's
            // per-axis travel away from the anchor. An axis whose start offset
            // is ~0 (the press was aligned with the anchor on that axis) can't
            // be driven meaningfully, so it stays at 1.
            let factorX =
                Math.abs(scale.startDx) >= SCALE_MIN_AXIS_DIST
                    ? dx / scale.startDx
                    : 1;
            let factorY =
                Math.abs(scale.startDy) >= SCALE_MIN_AXIS_DIST
                    ? dy / scale.startDy
                    : 1;
            // Shift snaps the scale to the nearest 45° of the drag direction:
            // a near-horizontal drag scales X only, near-vertical scales Y
            // only, and a near-diagonal drag scales uniformly. The snapped
            // octant (0..7, each 45° wide) decides which: even octants
            // (0/2/4/6) are the axes, odd octants (1/3/5/7) the diagonals.
            if (e.shiftKey) {
                const octant =
                    ((Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) % 8) + 8) %
                    8;
                if (octant === 0 || octant === 4) {
                    factorY = 1; // horizontal: X only
                } else if (octant === 2 || octant === 6) {
                    factorX = 1; // vertical: Y only
                } else {
                    // diagonal: uniform, driven by the radial distance ratio so
                    // it stays proportional even if one start axis was ~0.
                    const startDist = Math.hypot(scale.startDx, scale.startDy);
                    const dist = Math.hypot(dx, dy);
                    const uniform = startDist > 0 ? dist / startDist : 1;
                    factorX = uniform;
                    factorY = uniform;
                }
            }
            // Scale a point's offset from the anchor by the per-axis factors.
            const scalePoint = (p: Vec2): Vec2 =>
                scalePointAbout(p, anchor, factorX, factorY);
            if (scale.movers.length > 0) {
                moveNodes(
                    scale.movers.map((m) => ({
                        id: m.id,
                        pos: scalePoint(m.start),
                    })),
                );
            }
            // Ellipses scale their center offset and stretch each radius by its
            // own axis factor (rx with X, ry with Y).
            if (scale.ellipseMovers.length > 0) {
                moveEllipses(
                    scale.ellipseMovers.map((m) => ({
                        id: m.id,
                        center: scalePoint(m.start),
                        rx: m.rx * factorX,
                        ry: m.ry * factorY,
                    })),
                );
            }
            // Lines and arcs scale both endpoints (the arc's sweep is a
            // fraction, so its bulge scales with the chord automatically).
            if (scale.lineMovers.length > 0) {
                moveLines(
                    scale.lineMovers.map((m) => ({
                        id: m.id,
                        start: scalePoint(m.start),
                        end: scalePoint(m.end),
                    })),
                );
            }
            if (scale.arcMovers.length > 0) {
                moveArcs(
                    scale.arcMovers.map((m) => ({
                        id: m.id,
                        start: scalePoint(m.start),
                        end: scalePoint(m.end),
                    })),
                );
            }
            // Rects scale both corners (the box stays axis-aligned).
            if (scale.rectMovers.length > 0) {
                moveRects(
                    scale.rectMovers.map((m) => ({
                        id: m.id,
                        start: scalePoint(m.start),
                        end: scalePoint(m.end),
                    })),
                );
            }
            // Polygons scale every vertex about the anchor.
            if (scale.polygonMovers.length > 0) {
                movePolygons(
                    scale.polygonMovers.map((m) => ({
                        id: m.id,
                        points: m.points.map(scalePoint),
                    })),
                );
            }
            return;
        }
        const rotate = rotateRef.current;
        if (rotate) {
            const svg = svgRef.current;
            if (!svg) return;
            const pt = clientToSvgPoint(svg, e.clientX, e.clientY);
            const angle = Math.atan2(
                pt.y - rotate.pivot.y,
                pt.x - rotate.pivot.x,
            );
            let delta = angle - rotate.startAngle;
            // Shift snaps the rotation to 15° increments.
            if (e.shiftKey) {
                const step = Math.PI / 12;
                delta = Math.round(delta / step) * step;
            }
            const { pivot } = rotate;
            // Rotate a point's offset from the pivot by the drag angle.
            const rotatePoint = (p: Vec2): Vec2 =>
                rotatePointAbout(p, pivot, delta);
            // Under symmetry, re-mirror a shape's rotated defining points so its
            // counterparts (captured at press time, paired by `mirrorPointSets`
            // order) track as true reflections. `mirrors[i]` is "" when no copy
            // was found for that mirror, so it is skipped.
            const sym = rotate.symmetry;
            const { w, h } = canvasDimsRef.current;
            const center = { x: w / 2, y: h / 2 };
            const mirroredUpdatesTwoPoint = (
                rotatedStart: Vec2,
                rotatedEnd: Vec2,
                mirrors: string[],
            ) =>
                mirroredTwoPointUpdates(
                    rotatedStart,
                    rotatedEnd,
                    mirrors,
                    sym,
                    center,
                );
            if (rotate.movers.length > 0) {
                moveNodes(
                    rotate.movers.map((m) => ({
                        id: m.id,
                        pos: rotatePoint(m.start),
                    })),
                );
            }
            // Ellipses orbit their center around the pivot and spin their body by
            // the same delta (via `rotation`), so a non-circular ellipse truly
            // turns; startAngle advances too so any partial-arc carve spins with
            // it. A mirror copy negates the body delta when `flipped` so it turns
            // as a true reflection.
            if (rotate.ellipseMovers.length > 0) {
                const deltaDeg = (delta * 180) / Math.PI;
                const centers: Array<{ id: string; center: Vec2 }> = [];
                const updates: Array<{
                    id: string;
                    startAngle: number;
                    rotation: number;
                }> = [];
                for (const m of rotate.ellipseMovers) {
                    const rotatedCenter = rotatePoint(m.start);
                    centers.push({ id: m.id, center: rotatedCenter });
                    updates.push({
                        id: m.id,
                        startAngle: m.startAngleDeg + deltaDeg,
                        rotation: m.startRotationDeg + deltaDeg,
                    });
                    // Each mirror copy orbits to the reflection of the rotated
                    // center and spins its own carve / body by the same delta.
                    const mirroredCenters = mirrorPositions(
                        rotatedCenter,
                        sym,
                        center,
                    );
                    for (let i = 0; i < m.mirrors.length; i += 1) {
                        const copy = m.mirrors[i];
                        const mc = mirroredCenters[i];
                        if (!copy || !mc) continue;
                        const sign = copy.flipped ? -1 : 1;
                        centers.push({ id: copy.id, center: mc });
                        updates.push({
                            id: copy.id,
                            startAngle: copy.startAngleDeg + deltaDeg,
                            rotation: copy.startRotationDeg + sign * deltaDeg,
                        });
                    }
                }
                moveEllipses(centers);
                for (const u of updates) {
                    updateEllipse(u.id, {
                        startAngle: u.startAngle,
                        rotation: u.rotation,
                    });
                }
            }
            // Lines and arcs rotate both endpoints (the arc's sweep is a
            // fraction of the chord, so its bulge follows automatically).
            if (rotate.lineMovers.length > 0) {
                moveLines(
                    rotate.lineMovers.flatMap((m) => {
                        const start = rotatePoint(m.start);
                        const end = rotatePoint(m.end);
                        return [
                            { id: m.id, start, end },
                            ...mirroredUpdatesTwoPoint(start, end, m.mirrors),
                        ];
                    }),
                );
            }
            if (rotate.arcMovers.length > 0) {
                moveArcs(
                    rotate.arcMovers.flatMap((m) => {
                        const start = rotatePoint(m.start);
                        const end = rotatePoint(m.end);
                        return [
                            { id: m.id, start, end },
                            ...mirroredUpdatesTwoPoint(start, end, m.mirrors),
                        ];
                    }),
                );
            }
            // Rects orbit their center around the pivot and spin their body by
            // the same delta (via `rotation`), so the box keeps its size and
            // truly turns rather than resizing as its diagonal spins. A mirror
            // copy negates the body delta when `flipped` so it turns as a true
            // reflection. The box stays axis-aligned in start/end (handles, fills,
            // and corner radii keep working); only the rendered transform tilts.
            const deltaDegRect = (delta * 180) / Math.PI;
            if (rotate.rectMovers.length > 0) {
                const boxUpdates: Array<{
                    id: string;
                    start: Vec2;
                    end: Vec2;
                }> = [];
                const rotUpdates: Array<{ id: string; rotation: number }> = [];
                // Rebuild an axis-aligned box of the same half-extents centered
                // on `c` (the box's defining start/end are two opposite corners).
                const boxAround = (
                    c: Vec2,
                    halfW: number,
                    halfH: number,
                ): { start: Vec2; end: Vec2 } => ({
                    start: { x: c.x - halfW, y: c.y - halfH },
                    end: { x: c.x + halfW, y: c.y + halfH },
                });
                for (const m of rotate.rectMovers) {
                    const c0 = {
                        x: (m.start.x + m.end.x) / 2,
                        y: (m.start.y + m.end.y) / 2,
                    };
                    const halfW = Math.abs(m.end.x - m.start.x) / 2;
                    const halfH = Math.abs(m.end.y - m.start.y) / 2;
                    const rotatedCenter = rotatePoint(c0);
                    const box = boxAround(rotatedCenter, halfW, halfH);
                    boxUpdates.push({ id: m.id, ...box });
                    rotUpdates.push({
                        id: m.id,
                        rotation: m.startRotationDeg + deltaDegRect,
                    });
                    const mirroredCenters = mirrorPositions(
                        rotatedCenter,
                        sym,
                        center,
                    );
                    for (let i = 0; i < m.mirrors.length; i += 1) {
                        const copy = m.mirrors[i];
                        const mc = mirroredCenters[i];
                        if (!copy || !mc) continue;
                        const sign = copy.flipped ? -1 : 1;
                        boxUpdates.push({
                            id: copy.id,
                            ...boxAround(mc, halfW, halfH),
                        });
                        rotUpdates.push({
                            id: copy.id,
                            rotation: copy.startRotationDeg + sign * deltaDegRect,
                        });
                    }
                }
                moveRects(boxUpdates);
                for (const u of rotUpdates) {
                    updateRect(u.id, { rotation: u.rotation });
                }
            }
            // Polygons rotate every vertex about the pivot, so they spin truly
            // (no bounding-box rebuild — each vertex follows the rotation).
            if (rotate.polygonMovers.length > 0) {
                movePolygons(
                    rotate.polygonMovers.flatMap((m) => {
                        const rotated = m.points.map(rotatePoint);
                        const sets = mirrorPointSets(rotated, sym, center);
                        const out: Array<{ id: string; points: Vec2[] }> = [
                            { id: m.id, points: rotated },
                        ];
                        for (let i = 0; i < m.mirrors.length; i += 1) {
                            const id = m.mirrors[i];
                            const set = sets[i];
                            if (!id || !set) continue;
                            out.push({ id, points: set.points });
                        }
                        return out;
                    }),
                );
            }
            return;
        }
        if (ellipseDrawStartRef.current) {
            const svg = svgRef.current;
            if (!svg) return;
            const start = ellipseDrawStartRef.current;
            const cur = clientToSvgPoint(svg, e.clientX, e.clientY);
            let w = Math.abs(cur.x - start.x);
            let h = Math.abs(cur.y - start.y);
            // Shift locks to a 1:1 bounding box (a circle).
            if (e.shiftKey) {
                const side = Math.max(w, h);
                w = side;
                h = side;
            }
            setEllipseDrawRect({
                x: cur.x < start.x ? start.x - w : start.x,
                y: cur.y < start.y ? start.y - h : start.y,
                w,
                h,
            });
            return;
        }
        if (twoPointDrawStartRef.current) {
            const svg = svgRef.current;
            if (!svg) return;
            const start = twoPointDrawStartRef.current;
            const cur = clientToSvgPoint(svg, e.clientX, e.clientY);
            let end = cur;
            // Shift locks the segment to a horizontal, vertical, or 45° angle.
            if (e.shiftKey) {
                const { x: dx, y: dy } = constrainDeltaToAxes(
                    cur.x - start.x,
                    cur.y - start.y,
                );
                end = { x: start.x + dx, y: start.y + dy };
            }
            setTwoPointDrawEnd(end);
            return;
        }
        const drag = dragRef.current;
        if (
            Math.abs(e.clientX - drag.downX) > MOVE_THRESHOLD ||
            Math.abs(e.clientY - drag.downY) > MOVE_THRESHOLD
        ) {
            drag.moved = true;
        }
        if (marqueeStartRef.current) {
            const svg = svgRef.current;
            if (!svg) return;
            const pt = clientToSvgPoint(svg, e.clientX, e.clientY);
            setMarqueeRect(rectFromPoints(marqueeStartRef.current, pt));
        }
    }, [
        tool,
        moveNodes,
        moveEllipses,
        updateEllipse,
        moveLines,
        moveArcs,
        moveRects,
        updateRect,
        movePolygons,
    ]);

    const handleBgPointerUp = useCallback(
        (e: React.PointerEvent) => {
            if (panRef.current) {
                panRef.current = null;
                try {
                    (e.currentTarget as Element).releasePointerCapture(
                        e.pointerId,
                    );
                } catch {
                    /* no-op */
                }
                return;
            }
            if (scaleRef.current) {
                scaleRef.current = null;
                setScaleAnchor(null);
                try {
                    (e.currentTarget as Element).releasePointerCapture(
                        e.pointerId,
                    );
                } catch {
                    /* no-op */
                }
                return;
            }
            if (rotateRef.current) {
                rotateRef.current = null;
                setRotateAnchor(null);
                try {
                    (e.currentTarget as Element).releasePointerCapture(
                        e.pointerId,
                    );
                } catch {
                    /* no-op */
                }
                return;
            }
            if (ellipseDrawStartRef.current) {
                const rect = ellipseDrawRect;
                ellipseDrawStartRef.current = null;
                setEllipseDrawRect(null);
                try {
                    (e.currentTarget as Element).releasePointerCapture(
                        e.pointerId,
                    );
                } catch {
                    /* no-op */
                }
                // Ignore a too-small drag (a click) so stray taps don't create
                // zero-size ellipses.
                if (rect && rect.w >= 2 && rect.h >= 2) {
                    addEllipse(
                        { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 },
                        rect.w / 2,
                        rect.h / 2,
                    );
                }
                return;
            }
            if (twoPointDrawStartRef.current) {
                const start = twoPointDrawStartRef.current;
                const end = twoPointDrawEnd;
                twoPointDrawStartRef.current = null;
                setTwoPointDrawEnd(null);
                try {
                    (e.currentTarget as Element).releasePointerCapture(
                        e.pointerId,
                    );
                } catch {
                    /* no-op */
                }
                // Ignore a too-short drag (a click) so stray taps don't create
                // zero-length lines/arcs/rects.
                if (
                    end &&
                    Math.hypot(end.x - start.x, end.y - start.y) >=
                        TWO_POINT_MIN_LENGTH
                ) {
                    if (tool === "arc") addArc(start, end);
                    else if (tool === "rect") addRect(start, end);
                    else if (tool === "heat-zone") addHeatZone(start, end);
                    else addLine(start, end);
                }
                return;
            }
            const drag = dragRef.current;
            // A node-, ellipse-, line-, arc-, rect-, or polygon-drag's pointerup
            // is handled by that element's own handler (it captured the
            // pointer), so ignore.
            if (
                drag.nodeId ||
                drag.ellipseId ||
                drag.lineId ||
                drag.arcId ||
                drag.rectId ||
                drag.polygonId
            ) {
                return;
            }

            if (marqueeStartRef.current) {
                const start = marqueeStartRef.current;
                const svg = svgRef.current;
                const groupMode = groupModeRef.current;
                marqueeStartRef.current = null;
                groupModeRef.current = null;
                setMarqueeRect(null);
                if (drag.moved && svg) {
                    const end = clientToSvgPoint(svg, e.clientX, e.clientY);
                    const rect = rectFromPoints(start, end);
                    const inRect = (p: Vec2) =>
                        p.x >= rect.x &&
                        p.x <= rect.x + rect.w &&
                        p.y >= rect.y &&
                        p.y <= rect.y + rect.h;
                    // The Group subtool's marquee grabs nodes only and runs the
                    // group operation chosen at drag start, rather than selecting
                    // a mixed shape set the way the Select subtool's marquee does.
                    // Hidden layers can't be marquee-grabbed (their elements
                    // aren't visible). Locked layers are filtered later in the
                    // context's selection setters.
                    if (groupMode) {
                        const nodeIds = doc.nodes
                            .filter(
                                (n) =>
                                    !n.spawnedFrom &&
                                    !layerHidden(n.layerId) &&
                                    inRect(n.position),
                            )
                            .map((n) => n.id);
                        groupNodesFromMarquee(nodeIds, groupMode);
                        return;
                    }
                    // A marquee grabs every shape type (nodes/ellipses by
                    // center, lines/arcs by endpoint midpoint) so a mixed set
                    // can be selected in one drag.
                    const nodeIds = doc.nodes
                        .filter(
                            (n) => !layerHidden(n.layerId) && inRect(n.position),
                        )
                        .map((n) => n.id);
                    const ellipseIds = doc.ellipses
                        .filter(
                            (el) =>
                                !layerHidden(el.layerId) && inRect(el.center),
                        )
                        .map((el) => el.id);
                    const midpoint = (a: Vec2, b: Vec2): Vec2 => ({
                        x: (a.x + b.x) / 2,
                        y: (a.y + b.y) / 2,
                    });
                    const lineIds = doc.lines
                        .filter(
                            (l) =>
                                !layerHidden(l.layerId) &&
                                inRect(midpoint(l.start, l.end)),
                        )
                        .map((l) => l.id);
                    const arcIds = doc.arcs
                        .filter(
                            (a) =>
                                !layerHidden(a.layerId) &&
                                inRect(midpoint(a.start, a.end)),
                        )
                        .map((a) => a.id);
                    const rectIds = doc.rects
                        .filter(
                            (r) =>
                                !layerHidden(r.layerId) &&
                                inRect(midpoint(r.start, r.end)),
                        )
                        .map((r) => r.id);
                    // Polygons select by their vertex centroid.
                    const centroid = (pts: Vec2[]): Vec2 => {
                        const sum = pts.reduce(
                            (acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }),
                            { x: 0, y: 0 },
                        );
                        return {
                            x: sum.x / pts.length,
                            y: sum.y / pts.length,
                        };
                    };
                    const polygonIds = doc.polygons
                        .filter(
                            (p) =>
                                !layerHidden(p.layerId) &&
                                p.points.length > 0 &&
                                inRect(centroid(p.points)),
                        )
                        .map((p) => p.id);
                    selectInRect(
                        nodeIds,
                        ellipseIds,
                        lineIds,
                        arcIds,
                        rectIds,
                        polygonIds,
                        e.shiftKey,
                    );
                } else if (!e.shiftKey) {
                    clearSelection();
                }
                return;
            }

            if (drag.moved) return;
            if (tool === "place-node") {
                const svg = svgRef.current;
                if (!svg) return;
                addNodeAt(clientToSvgPoint(svg, e.clientX, e.clientY));
            } else if (tool === "node-brush") {
                const svg = svgRef.current;
                if (!svg) return;
                applyNodeBrush(
                    clientToSvgPoint(svg, e.clientX, e.clientY),
                    nodeBrushOptions,
                );
            } else if (tool === "multi-node-brush") {
                const svg = svgRef.current;
                if (!svg) return;
                applyMultiNodeBrush(
                    clientToSvgPoint(svg, e.clientX, e.clientY),
                    multiNodeBrushOptions,
                    nodeBrushOptions,
                );
            } else if (tool === "mega-node-brush") {
                const svg = svgRef.current;
                if (!svg) return;
                applyMegaNodeBrush(
                    clientToSvgPoint(svg, e.clientX, e.clientY),
                    megaNodeBrushOptions,
                    multiNodeBrushOptions,
                    nodeBrushOptions,
                );
            }
        },
        [
            tool,
            addNodeAt,
            applyNodeBrush,
            nodeBrushOptions,
            applyMultiNodeBrush,
            multiNodeBrushOptions,
            applyMegaNodeBrush,
            megaNodeBrushOptions,
            doc.nodes,
            doc.ellipses,
            doc.lines,
            doc.arcs,
            doc.rects,
            doc.polygons,
            selectInRect,
            groupNodesFromMarquee,
            clearSelection,
            addEllipse,
            addLine,
            addArc,
            addRect,
            addHeatZone,
            ellipseDrawRect,
            twoPointDrawEnd,
            layerHidden,
        ],
    );

    /**
     * Capture the symmetric counterpart shapes for a set of whole-shape movers,
     * so a drag under active symmetry moves each counterpart in lockstep (the
     * shape analogue of how node movers carry `mirrorIds`). For each mover we
     * find, by geometry at press time, the existing mirror-copy shape for each
     * active mirror of `mirrorPointSets` — paired by that order, "" when none is
     * found. A shared `claimed` set (seeded with the selected/original ids) keeps
     * a selected original or an already-paired copy from being grabbed twice.
     *
     * Ellipses match by center alone (`mirrorPositions`); the two-point and
     * polygon kinds match their full ordered point list. The mirrors are pinned
     * here and never re-found per frame, so — exactly like the node drag — the
     * drag keeps driving the same counterparts even as a shape sweeps past
     * another symmetric shape. Stable: reads doc symmetry / canvas through refs.
     */
    const captureEllipseMirrors = useCallback(
        (movers: EllipseMover[]): void => {
            const sym = docRef.current.symmetry;
            const { w, h } = canvasDimsRef.current;
            const center = { x: w / 2, y: h / 2 };
            const claimed = new Set(movers.map((m) => m.id));
            for (const m of movers) {
                m.mirrors = mirrorPositions(m.start, sym, center).map((mp) => {
                    const match = findShapeNearPoints(
                        docRef.current.ellipses,
                        [mp],
                        (el) => [el.center],
                        undefined,
                        claimed,
                    );
                    if (!match) return "";
                    claimed.add(match.id);
                    return match.id;
                });
            }
        },
        [],
    );

    const captureTwoPointMirrors = useCallback(
        (
            movers: TwoPointMover[],
            pool: Array<{ id: string; start: Vec2; end: Vec2 }>,
        ): void => {
            const sym = docRef.current.symmetry;
            const { w, h } = canvasDimsRef.current;
            const center = { x: w / 2, y: h / 2 };
            const claimed = new Set(movers.map((m) => m.id));
            for (const m of movers) {
                m.mirrors = mirrorPointSets(
                    [m.start, m.end],
                    sym,
                    center,
                ).map((set) => {
                    const match = findShapeNearPoints(
                        pool,
                        set.points,
                        (s) => [s.start, s.end],
                        undefined,
                        claimed,
                    );
                    if (!match) return "";
                    claimed.add(match.id);
                    return match.id;
                });
            }
        },
        [],
    );

    const capturePolygonMirrors = useCallback(
        (movers: PolygonMover[]): void => {
            const sym = docRef.current.symmetry;
            const { w, h } = canvasDimsRef.current;
            const center = { x: w / 2, y: h / 2 };
            const claimed = new Set(movers.map((m) => m.id));
            for (const m of movers) {
                m.mirrors = mirrorPointSets(m.points, sym, center).map((set) => {
                    const match = findShapeNearPoints(
                        docRef.current.polygons,
                        set.points,
                        (p) => p.points,
                        undefined,
                        claimed,
                    );
                    if (!match) return "";
                    claimed.add(match.id);
                    return match.id;
                });
            }
        },
        [],
    );

    /**
     * Capture mirrors for every shape mover currently set on `drag`. Called once
     * after a grab handler populates the movers, so the subsequent drag re-mirrors
     * the counterparts. A no-op when symmetry is off (no mirror sets exist).
     */
    const captureDragShapeMirrors = useCallback(
        (drag: DragState): void => {
            captureEllipseMirrors(drag.ellipseMovers);
            captureTwoPointMirrors(drag.lineMovers, docRef.current.lines);
            captureTwoPointMirrors(drag.arcMovers, docRef.current.arcs);
            captureTwoPointMirrors(drag.rectMovers, docRef.current.rects);
            capturePolygonMirrors(drag.polygonMovers);
        },
        [captureEllipseMirrors, captureTwoPointMirrors, capturePolygonMirrors],
    );

    const handleNodePointerDown = useCallback(
        (e: React.PointerEvent, node: DiagramNode) => {
            // The scale tool operates on the whole selection from the
            // background handler, so let presses on a node bubble through to it
            // rather than starting a per-node interaction.
            if (tool === "scale") return;
            e.stopPropagation();
            const isSpawnClone = Boolean(node.spawnedFrom);
            const svg = svgRef.current;
            dragRef.current = {
                ...emptyDragState(),
                downX: e.clientX,
                downY: e.clientY,
                startPoint: svg
                    ? clientToSvgPoint(svg, e.clientX, e.clientY)
                    : null,
            };
            // Spawn clones are derived from a host pattern and can't be dragged;
            // every other node moves directly (moveNodes handles symmetry).
            if (tool === "select" && !isSpawnClone) {
                // Alt-drag duplicates: spawn a copy and drag that instead of
                // the original. Pointer capture stays on the original element,
                // so subsequent moves drive the captured drag state.
                if (e.altKey) {
                    const dup = duplicateNode(node.id, { alwaysMirror: true });
                    if (dup) {
                        dragRef.current.nodeId = dup.id;
                        dragRef.current.duplicated = true;
                        // Under symmetry, duplicateNode also dropped mirror
                        // copies; pin them as the mover's counterparts so the
                        // whole symmetric set drags together (matching paste).
                        dragRef.current.movers = [
                            {
                                id: dup.id,
                                start: { ...node.position },
                                mirrorIds: dup.mirrorIds,
                            },
                        ];
                    } else {
                        dragRef.current.nodeId = node.id;
                        pushSnapshot();
                    }
                } else {
                    dragRef.current.nodeId = node.id;
                    pushSnapshot();
                    // Drag the whole selection together when the grabbed node is
                    // part of a multi-selection; otherwise just this node. A
                    // mixed selection (nodes + ellipses/lines/arcs) drags every
                    // shape as one group.
                    const partOfSelection =
                        selectedNodeIds.includes(node.id) &&
                        (selectedNodeIds.length > 1 ||
                            selectedEllipseIds.length > 0 ||
                            selectedLineIds.length > 0 ||
                            selectedArcIds.length > 0 ||
                            selectedRectIds.length > 0 ||
                            selectedPolygonIds.length > 0);
                    const movers = partOfSelection
                        ? doc.nodes.filter(
                              (n) =>
                                  selectedNodeIds.includes(n.id) &&
                                  !n.spawnedFrom,
                          )
                        : [node];
                    // Pin each mover's symmetric counterparts now, by geometry,
                    // so the drag keeps driving those exact nodes instead of
                    // re-finding them every frame (which would latch onto a
                    // passed-over node's counterpart). The shared `claimed` set
                    // — seeded with the movers themselves — stops one node from
                    // being grabbed as two movers' counterpart.
                    const mirrorClaimed = new Set<string>(
                        movers.map((n) => n.id),
                    );
                    dragRef.current.movers = movers.map((n) => ({
                        id: n.id,
                        start: { ...n.position },
                        mirrorIds: findNodeMirrorIds(n.id, mirrorClaimed),
                    }));
                    if (partOfSelection) {
                        dragRef.current.ellipseMovers = doc.ellipses
                            .filter((el) => selectedEllipseIds.includes(el.id))
                            .map((el) => ({ id: el.id, start: { ...el.center } }));
                        dragRef.current.lineMovers = doc.lines
                            .filter((l) => selectedLineIds.includes(l.id))
                            .map((l) => ({
                                id: l.id,
                                start: { ...l.start },
                                end: { ...l.end },
                            }));
                        dragRef.current.arcMovers = doc.arcs
                            .filter((a) => selectedArcIds.includes(a.id))
                            .map((a) => ({
                                id: a.id,
                                start: { ...a.start },
                                end: { ...a.end },
                            }));
                        dragRef.current.rectMovers = doc.rects
                            .filter((r) => selectedRectIds.includes(r.id))
                            .map((r) => ({
                                id: r.id,
                                start: { ...r.start },
                                end: { ...r.end },
                            }));
                        dragRef.current.polygonMovers = doc.polygons
                            .filter((p) => selectedPolygonIds.includes(p.id))
                            .map((p) => ({
                                id: p.id,
                                points: p.points.map((pt) => ({ ...pt })),
                            }));
                        // Pin the shape movers' symmetric counterparts too, so
                        // the mixed-selection drag mirrors every shape kind.
                        captureDragShapeMirrors(dragRef.current);
                    }
                }
                (e.target as Element).setPointerCapture(e.pointerId);
            }
        },
        [
            tool,
            pushSnapshot,
            duplicateNode,
            findNodeMirrorIds,
            captureDragShapeMirrors,
            selectedNodeIds,
            selectedEllipseIds,
            selectedLineIds,
            selectedArcIds,
            selectedRectIds,
            selectedPolygonIds,
            doc.nodes,
            doc.ellipses,
            doc.lines,
            doc.arcs,
            doc.rects,
            doc.polygons,
        ],
    );

    /**
     * Core snapper: snap a raw drag `dx`/`dy` so `movingPoints` align to nearby
     * static geometry. Static targets are every node/ellipse center and line/
     * arc/rect/polygon point in the document EXCEPT the shapes whose ids are in
     * the `exclude` sets (the ones being dragged), plus the canvas centerlines
     * and edges. Guide spans cover the full canvas so the lines read clearly.
     * Shared by whole-shape drags (`applyDragSnap`) and single-point handle
     * drags (`applyPointSnap`). Stable: reads doc/canvas/zoom through refs.
     */
    const computeSnap = useCallback(
        (
            movingPoints: Vec2[],
            exclude: {
                nodes?: ReadonlySet<string>;
                ellipses?: ReadonlySet<string>;
                lines?: ReadonlySet<string>;
                arcs?: ReadonlySet<string>;
                rects?: ReadonlySet<string>;
                polygons?: ReadonlySet<string>;
            },
            dx: number,
            dy: number,
        ): SnapResult => {
            if (movingPoints.length === 0) return { dx, dy, guides: [] };
            const d = docRef.current;
            const { w, h } = canvasDimsRef.current;
            const has = (s: ReadonlySet<string> | undefined, id: string) =>
                s ? s.has(id) : false;

            // Threshold in canvas units: a fixed screen distance / current zoom.
            const zoom = viewBoxWidth / viewRef.current.w;
            const threshold = SNAP_SCREEN_PX / zoom;

            // Static targets: every defining point not on an excluded shape.
            const staticPoints: Vec2[] = [];
            for (const n of d.nodes) {
                if (!has(exclude.nodes, n.id) && !n.spawnedFrom) {
                    staticPoints.push(n.position);
                }
            }
            for (const el of d.ellipses) {
                if (!has(exclude.ellipses, el.id)) staticPoints.push(el.center);
            }
            for (const l of d.lines) {
                if (!has(exclude.lines, l.id)) staticPoints.push(l.start, l.end);
            }
            for (const a of d.arcs) {
                if (!has(exclude.arcs, a.id)) staticPoints.push(a.start, a.end);
            }
            for (const r of d.rects) {
                if (!has(exclude.rects, r.id)) staticPoints.push(r.start, r.end);
            }
            for (const p of d.polygons) {
                if (!has(exclude.polygons, p.id)) staticPoints.push(...p.points);
            }

            // Guides span the whole canvas so they read as full-height/width.
            const xLines: SnapLine[] = staticPoints.map((p) => ({
                coord: p.x,
                min: 0,
                max: h,
            }));
            const yLines: SnapLine[] = staticPoints.map((p) => ({
                coord: p.y,
                min: 0,
                max: w,
            }));
            // Canvas centerlines and edges.
            xLines.push(
                { coord: 0, min: 0, max: h },
                { coord: w / 2, min: 0, max: h },
                { coord: w, min: 0, max: h },
            );
            yLines.push(
                { coord: 0, min: 0, max: w },
                { coord: h / 2, min: 0, max: w },
                { coord: h, min: 0, max: w },
            );

            return snapDelta(movingPoints, dx, dy, xLines, yLines, threshold);
        },
        [viewBoxWidth],
    );

    /**
     * Build the snap candidate lines for a whole-shape drag and run the core
     * snapper. Excludes every dragged shape from the static targets, plus — under
     * symmetry — the mirror counterparts each moving node/shape drags in lockstep
     * (they aren't in `*Movers`, so without this a shape would try to snap to its
     * own moving mirror and the pair would skip). Node counterparts are found by
     * geometry; shape counterparts are the ids pinned on each mover's `mirrors`.
     */
    const applyDragSnap = useCallback(
        (drag: DragState, dx: number, dy: number): SnapResult => {
            const d = docRef.current;
            const { w, h } = canvasDimsRef.current;
            const movingNodeIds = new Set(drag.movers.map((m) => m.id));
            const center = { x: w / 2, y: h / 2 };
            for (const m of drag.movers) {
                for (const mp of mirrorPositions(m.start, d.symmetry, center)) {
                    const counterpart = findNodeNearPosition(d.nodes, mp);
                    if (counterpart) movingNodeIds.add(counterpart.id);
                }
            }

            // A set of every mover id plus its pinned mirror counterpart ids, so
            // the moving shape never snaps to its own moving reflection.
            const withMirrors = (
                movers: Array<{ id: string; mirrors?: string[] }>,
            ): Set<string> => {
                const ids = new Set<string>();
                for (const m of movers) {
                    ids.add(m.id);
                    for (const mid of m.mirrors ?? []) {
                        if (mid) ids.add(mid);
                    }
                }
                return ids;
            };

            // The points actually being dragged (their start positions). Lines
            // and arcs contribute both endpoints so either end can snap.
            const movingPoints: Vec2[] = [
                ...drag.movers.map((m) => m.start),
                ...drag.ellipseMovers.map((m) => m.start),
                ...drag.lineMovers.flatMap((m) => [m.start, m.end]),
                ...drag.arcMovers.flatMap((m) => [m.start, m.end]),
                ...drag.rectMovers.flatMap((m) => [m.start, m.end]),
                ...drag.polygonMovers.flatMap((m) => m.points),
            ];

            return computeSnap(
                movingPoints,
                {
                    nodes: movingNodeIds,
                    ellipses: withMirrors(drag.ellipseMovers),
                    lines: withMirrors(drag.lineMovers),
                    arcs: withMirrors(drag.arcMovers),
                    rects: withMirrors(drag.rectMovers),
                    polygons: withMirrors(drag.polygonMovers),
                },
                dx,
                dy,
            );
        },
        [computeSnap],
    );

    /**
     * Snap a single-point (handle) drag. Only the one dragged point snaps, so
     * `movingPoints` is just it; the shape being edited is excluded from the
     * static targets so the point doesn't snap to its own siblings (e.g. a line
     * endpoint to the line's other endpoint, or a rect corner to its own box).
     */
    const applyPointSnap = useCallback(
        (
            kind: ShapePointKind,
            id: string,
            point: Vec2,
            dx: number,
            dy: number,
        ): SnapResult => {
            const ids: ReadonlySet<string> = new Set([id]);
            const exclude =
                kind === "line"
                    ? { lines: ids }
                    : kind === "arc"
                      ? { arcs: ids }
                      : kind === "rect"
                        ? { rects: ids }
                        : kind === "polygon"
                          ? { polygons: ids }
                          : // heat zones aren't snap targets, so nothing to exclude.
                            {};
            return computeSnap([point], exclude, dx, dy);
        },
        [computeSnap],
    );

    /**
     * Drag-move handler shared by grabbed nodes and ellipses. Applies one delta
     * to every mover (nodes via `moveNodes`, ellipses via `moveEllipses`), so a
     * mixed selection moves together while keeping its relative layout. Under
     * active symmetry each shape mover's pinned counterparts (see
     * `captureDragShapeMirrors`) are re-mirrored from the translated geometry so
     * they track as true reflections — matching how `moveNodes` drags node
     * counterparts via `mirrorIds`.
     */
    const handleGrabPointerMove = useCallback(
        (e: React.PointerEvent) => {
            const drag = dragRef.current;
            if (
                Math.abs(e.clientX - drag.downX) > MOVE_THRESHOLD ||
                Math.abs(e.clientY - drag.downY) > MOVE_THRESHOLD
            ) {
                drag.moved = true;
            }
            if (
                !drag.nodeId &&
                !drag.ellipseId &&
                !drag.lineId &&
                !drag.arcId &&
                !drag.rectId &&
                !drag.polygonId
            ) {
                return;
            }
            if (!drag.startPoint) return;
            if (
                drag.movers.length === 0 &&
                drag.ellipseMovers.length === 0 &&
                drag.lineMovers.length === 0 &&
                drag.arcMovers.length === 0 &&
                drag.rectMovers.length === 0 &&
                drag.polygonMovers.length === 0
            ) {
                return;
            }
            const svg = svgRef.current;
            if (!svg) return;
            const cur = clientToSvgPoint(svg, e.clientX, e.clientY);
            let dx = cur.x - drag.startPoint.x;
            let dy = cur.y - drag.startPoint.y;
            // Shift locks the drag to a horizontal, vertical, or 45° diagonal path.
            if (e.shiftKey) {
                ({ x: dx, y: dy } = constrainDeltaToAxes(dx, dy));
            }
            // Snapping: align the moving points to nearby static nodes/ellipses
            // and the canvas centerlines / edges. Disabled while shift is held
            // (axis-lock owns the delta then) and when the user has it off.
            if (snapEnabledRef.current && !e.shiftKey) {
                const guides = applyDragSnap(drag, dx, dy);
                dx = guides.dx;
                dy = guides.dy;
                setSnapGuides(guides.guides);
            } else if (snapGuidesActiveRef.current) {
                setSnapGuides([]);
            }
            // Apply the same delta to every mover, preserving relative layout.
            // `mirrorIds` (pinned at drag start) keeps each mover's symmetric
            // counterparts locked in across the whole drag.
            if (drag.movers.length > 0) {
                moveNodes(
                    drag.movers.map((m) => ({
                        id: m.id,
                        pos: { x: m.start.x + dx, y: m.start.y + dy },
                        mirrorIds: m.mirrorIds,
                    })),
                );
            }
            // Under symmetry, re-mirror each shape mover's translated geometry so
            // its counterparts (pinned at drag start via captureDragShapeMirrors,
            // paired by `mirrorPointSets` order) track as true reflections.
            // `mirrors[i]` is "" when no copy was found for that mirror, so it is
            // skipped. Built once here and reused by every shape kind below.
            const sym = docRef.current.symmetry;
            const { w: cw, h: ch } = canvasDimsRef.current;
            const center = { x: cw / 2, y: ch / 2 };
            const mirroredTwoPoint = (
                movedStart: Vec2,
                movedEnd: Vec2,
                mirrors: string[] | undefined,
            ) =>
                mirroredTwoPointUpdates(
                    movedStart,
                    movedEnd,
                    mirrors,
                    sym,
                    center,
                );
            if (drag.ellipseMovers.length > 0) {
                moveEllipses(
                    drag.ellipseMovers.flatMap((m) => {
                        const movedCenter = {
                            x: m.start.x + dx,
                            y: m.start.y + dy,
                        };
                        const out = [{ id: m.id, center: movedCenter }];
                        // Each mirror copy's center reflects the moved center.
                        if (m.mirrors && m.mirrors.length > 0) {
                            const mirrored = mirrorPositions(
                                movedCenter,
                                sym,
                                center,
                            );
                            for (let i = 0; i < m.mirrors.length; i += 1) {
                                const id = m.mirrors[i];
                                const mc = mirrored[i];
                                if (!id || !mc) continue;
                                out.push({ id, center: mc });
                            }
                        }
                        return out;
                    }),
                );
            }
            // Lines and arcs translate: shift both endpoints by the same delta.
            if (drag.lineMovers.length > 0) {
                moveLines(
                    drag.lineMovers.flatMap((m) => {
                        const start = { x: m.start.x + dx, y: m.start.y + dy };
                        const end = { x: m.end.x + dx, y: m.end.y + dy };
                        return [
                            { id: m.id, start, end },
                            ...mirroredTwoPoint(start, end, m.mirrors),
                        ];
                    }),
                );
            }
            if (drag.arcMovers.length > 0) {
                moveArcs(
                    drag.arcMovers.flatMap((m) => {
                        const start = { x: m.start.x + dx, y: m.start.y + dy };
                        const end = { x: m.end.x + dx, y: m.end.y + dy };
                        return [
                            { id: m.id, start, end },
                            ...mirroredTwoPoint(start, end, m.mirrors),
                        ];
                    }),
                );
            }
            // Rects translate: shift both corners by the same delta.
            if (drag.rectMovers.length > 0) {
                moveRects(
                    drag.rectMovers.flatMap((m) => {
                        const start = { x: m.start.x + dx, y: m.start.y + dy };
                        const end = { x: m.end.x + dx, y: m.end.y + dy };
                        return [
                            { id: m.id, start, end },
                            ...mirroredTwoPoint(start, end, m.mirrors),
                        ];
                    }),
                );
            }
            // Polygons translate: shift every vertex by the same delta.
            if (drag.polygonMovers.length > 0) {
                movePolygons(
                    drag.polygonMovers.flatMap((m) => {
                        const points = m.points.map((p) => ({
                            x: p.x + dx,
                            y: p.y + dy,
                        }));
                        const out: Array<{ id: string; points: Vec2[] }> = [
                            { id: m.id, points },
                        ];
                        if (m.mirrors && m.mirrors.length > 0) {
                            const sets = mirrorPointSets(points, sym, center);
                            for (let i = 0; i < m.mirrors.length; i += 1) {
                                const id = m.mirrors[i];
                                const set = sets[i];
                                if (!id || !set) continue;
                                out.push({ id, points: set.points });
                            }
                        }
                        return out;
                    }),
                );
            }
        },
        [
            moveNodes,
            moveEllipses,
            moveLines,
            moveArcs,
            moveRects,
            movePolygons,
            applyDragSnap,
        ],
    );

    const handleNodePointerUp = useCallback(
        (e: React.PointerEvent, node: DiagramNode) => {
            const drag = dragRef.current;
            const wasDragging = drag.nodeId != null;
            if (wasDragging) {
                try {
                    (e.target as Element).releasePointerCapture(e.pointerId);
                } catch {
                    /* no-op */
                }
            }
            // A plain click (no movement) that didn't spawn a duplicate handles
            // selection / connect. After an alt-drag the duplicate is already
            // selected, so leave the selection alone.
            if (!drag.moved && !drag.duplicated) {
                if (tool === "connect") {
                    handleNodePick(node.id);
                } else if (tool === "interact") {
                    // Dispatch one attract/repel pulse on the clicked node.
                    applyNodeInteraction(node.id);
                } else if (tool === "select" || tool === "connect-selection") {
                    if (e.shiftKey) toggleNodeSelection(node.id);
                    else setSelectedNodeId(node.id);
                }
            }
            dragRef.current = emptyDragState();
            if (snapGuidesActiveRef.current) setSnapGuides([]);
        },
        [
            tool,
            handleNodePick,
            applyNodeInteraction,
            toggleNodeSelection,
            setSelectedNodeId,
        ],
    );

    // Double-click a node to select it and open its inspector panel (matching
    // the node list's "open inspector" affordance in the controls).
    const handleNodeDoubleClick = useCallback(
        (e: React.MouseEvent, node: DiagramNode) => {
            if (tool !== "select") return;
            e.stopPropagation();
            setSelectedNodeId(node.id);
            setNodePanelOpen(true);
        },
        [tool, setSelectedNodeId, setNodePanelOpen],
    );

    // --- Ellipse element interactions (select tool) ----------------------

    const handleEllipsePointerDown = useCallback(
        (e: React.PointerEvent, ellipse: DiagramEllipse) => {
            // Only the select tool grabs ellipses; other tools act on the
            // background (e.g. the ellipse tool draws a fresh one).
            if (tool !== "select") return;
            e.stopPropagation();
            const svg = svgRef.current;
            dragRef.current = {
                ...emptyDragState(),
                ellipseId: ellipse.id,
                downX: e.clientX,
                downY: e.clientY,
                startPoint: svg
                    ? clientToSvgPoint(svg, e.clientX, e.clientY)
                    : null,
            };
            // Alt-drag duplicates: spawn a copy and drag that instead of the
            // original (matching node alt-drag). The copy's start tracks the
            // original center so the drag delta lands it under the pointer.
            // duplicateEllipse pushes its own snapshot, so don't push again here.
            if (e.altKey) {
                const newId = duplicateEllipse(ellipse.id);
                if (newId) {
                    dragRef.current.ellipseId = newId;
                    dragRef.current.duplicated = true;
                    dragRef.current.ellipseMovers = [
                        { id: newId, start: { ...ellipse.center } },
                    ];
                    (e.target as Element).setPointerCapture(e.pointerId);
                    return;
                }
                pushSnapshot();
            } else {
                pushSnapshot();
            }
            // Drag the whole selection together when the grabbed ellipse is part
            // of a multi-selection; otherwise just this ellipse. A mixed
            // selection (nodes + ellipses/lines/arcs) drags every shape together.
            const partOfSelection =
                selectedEllipseIds.includes(ellipse.id) &&
                (selectedEllipseIds.length > 1 ||
                    selectedNodeIds.length > 0 ||
                    selectedLineIds.length > 0 ||
                    selectedArcIds.length > 0 ||
                    selectedRectIds.length > 0 ||
                    selectedPolygonIds.length > 0);
            const ellipses = partOfSelection
                ? doc.ellipses.filter((el) =>
                      selectedEllipseIds.includes(el.id),
                  )
                : [ellipse];
            dragRef.current.ellipseMovers = ellipses.map((el) => ({
                id: el.id,
                start: { ...el.center },
            }));
            if (partOfSelection) {
                dragRef.current.movers = doc.nodes
                    .filter(
                        (n) =>
                            selectedNodeIds.includes(n.id) && !n.spawnedFrom,
                    )
                    .map((n) => ({ id: n.id, start: { ...n.position } }));
                dragRef.current.lineMovers = doc.lines
                    .filter((l) => selectedLineIds.includes(l.id))
                    .map((l) => ({
                        id: l.id,
                        start: { ...l.start },
                        end: { ...l.end },
                    }));
                dragRef.current.arcMovers = doc.arcs
                    .filter((a) => selectedArcIds.includes(a.id))
                    .map((a) => ({
                        id: a.id,
                        start: { ...a.start },
                        end: { ...a.end },
                    }));
                dragRef.current.rectMovers = doc.rects
                    .filter((r) => selectedRectIds.includes(r.id))
                    .map((r) => ({
                        id: r.id,
                        start: { ...r.start },
                        end: { ...r.end },
                    }));
                dragRef.current.polygonMovers = doc.polygons
                    .filter((p) => selectedPolygonIds.includes(p.id))
                    .map((p) => ({
                        id: p.id,
                        points: p.points.map((pt) => ({ ...pt })),
                    }));
            }
            // Pin every shape mover's symmetric counterparts so the drag mirrors
            // them in lockstep (covers a lone ellipse and a mixed selection).
            captureDragShapeMirrors(dragRef.current);
            (e.target as Element).setPointerCapture(e.pointerId);
        },
        [
            tool,
            pushSnapshot,
            duplicateEllipse,
            captureDragShapeMirrors,
            selectedEllipseIds,
            selectedNodeIds,
            selectedLineIds,
            selectedArcIds,
            selectedRectIds,
            selectedPolygonIds,
            doc.ellipses,
            doc.nodes,
            doc.lines,
            doc.arcs,
            doc.rects,
            doc.polygons,
        ],
    );

    const handleEllipsePointerUp = useCallback(
        (e: React.PointerEvent, ellipse: DiagramEllipse) => {
            const wasDragging = dragRef.current.ellipseId != null;
            const moved = dragRef.current.moved;
            const duplicated = dragRef.current.duplicated;
            if (wasDragging) {
                try {
                    (e.target as Element).releasePointerCapture(e.pointerId);
                } catch {
                    /* no-op */
                }
            }
            // A plain click (no movement) selects the ellipse; shift toggles it
            // in/out of the current selection. After an alt-drag the duplicate is
            // already selected, so leave the selection alone.
            if (!moved && !duplicated && tool === "select") {
                if (e.shiftKey) toggleEllipseSelection(ellipse.id);
                else setSelectedEllipseId(ellipse.id);
            }
            dragRef.current = emptyDragState();
            if (snapGuidesActiveRef.current) setSnapGuides([]);
        },
        [tool, setSelectedEllipseId, toggleEllipseSelection],
    );

    // --- Line & arc element interactions (select tool) -------------------
    // Lines and arcs share the ellipse's grab/drag model, but as two-point
    // shapes they translate both endpoints together (TwoPointMover). The two
    // kinds behave identically, so one parameterized pair of handlers serves
    // both — `kind` decides which selection state and mover list to populate.

    const handleTwoPointPointerDown = useCallback(
        (
            e: React.PointerEvent,
            kind: "line" | "arc" | "rect",
            shape: DiagramLine | DiagramArc | DiagramRect,
        ) => {
            // Only the select tool grabs lines/arcs/rects; other tools act on the
            // background (e.g. the line tool draws a fresh one).
            if (tool !== "select") return;
            e.stopPropagation();
            const idField =
                kind === "line" ? "lineId" : kind === "arc" ? "arcId" : "rectId";
            const moverField =
                kind === "line"
                    ? "lineMovers"
                    : kind === "arc"
                      ? "arcMovers"
                      : "rectMovers";
            const svg = svgRef.current;
            dragRef.current = {
                ...emptyDragState(),
                [idField]: shape.id,
                downX: e.clientX,
                downY: e.clientY,
                startPoint: svg
                    ? clientToSvgPoint(svg, e.clientX, e.clientY)
                    : null,
            };
            const selectedIds =
                kind === "line"
                    ? selectedLineIds
                    : kind === "arc"
                      ? selectedArcIds
                      : selectedRectIds;
            const duplicateShape = (id: string) =>
                kind === "line"
                    ? duplicateLine(id)
                    : kind === "arc"
                      ? duplicateArc(id)
                      : duplicateRect(id);
            const toMover = (s: { id: string; start: Vec2; end: Vec2 }) => ({
                id: s.id,
                start: { ...s.start },
                end: { ...s.end },
            });
            // Alt-drag duplicates: spawn a copy and drag that instead of the
            // original (matching node/ellipse alt-drag). The copy's movers track
            // the original endpoints so the drag delta lands it under the pointer.
            // duplicate* push their own snapshot, so skip it here.
            if (e.altKey) {
                const newId = duplicateShape(shape.id);
                if (newId) {
                    dragRef.current.duplicated = true;
                    dragRef.current[idField] = newId;
                    dragRef.current[moverField] = [
                        {
                            id: newId,
                            start: { ...shape.start },
                            end: { ...shape.end },
                        },
                    ];
                    (e.target as Element).setPointerCapture(e.pointerId);
                    return;
                }
                pushSnapshot();
            } else {
                pushSnapshot();
            }
            // Drag the whole selection together when the grabbed shape is part
            // of a multi-selection; otherwise just this shape. A mixed selection
            // drags every shape type as one group.
            const otherTwoPoint =
                (kind !== "line" && selectedLineIds.length > 0) ||
                (kind !== "arc" && selectedArcIds.length > 0) ||
                (kind !== "rect" && selectedRectIds.length > 0);
            const partOfSelection =
                selectedIds.includes(shape.id) &&
                (selectedIds.length > 1 ||
                    selectedNodeIds.length > 0 ||
                    selectedEllipseIds.length > 0 ||
                    selectedPolygonIds.length > 0 ||
                    otherTwoPoint);

            // Always populate the grabbed kind's movers (just this shape when not
            // part of a multi-selection).
            dragRef.current[moverField] = [toMover(shape)];
            if (partOfSelection) {
                dragRef.current.movers = doc.nodes
                    .filter(
                        (n) =>
                            selectedNodeIds.includes(n.id) && !n.spawnedFrom,
                    )
                    .map((n) => ({ id: n.id, start: { ...n.position } }));
                dragRef.current.ellipseMovers = doc.ellipses
                    .filter((el) => selectedEllipseIds.includes(el.id))
                    .map((el) => ({ id: el.id, start: { ...el.center } }));
                // Carry every two-point kind that's selected (the grabbed kind
                // included, so its full selection rather than just this shape).
                dragRef.current.lineMovers = doc.lines
                    .filter((l) => selectedLineIds.includes(l.id))
                    .map(toMover);
                dragRef.current.arcMovers = doc.arcs
                    .filter((a) => selectedArcIds.includes(a.id))
                    .map(toMover);
                dragRef.current.rectMovers = doc.rects
                    .filter((r) => selectedRectIds.includes(r.id))
                    .map(toMover);
                dragRef.current.polygonMovers = doc.polygons
                    .filter((p) => selectedPolygonIds.includes(p.id))
                    .map((p) => ({
                        id: p.id,
                        points: p.points.map((pt) => ({ ...pt })),
                    }));
            }
            // Pin every shape mover's symmetric counterparts so the drag mirrors
            // them in lockstep (covers a lone shape and a mixed selection).
            captureDragShapeMirrors(dragRef.current);
            (e.target as Element).setPointerCapture(e.pointerId);
        },
        [
            tool,
            pushSnapshot,
            duplicateLine,
            duplicateArc,
            duplicateRect,
            captureDragShapeMirrors,
            selectedLineIds,
            selectedArcIds,
            selectedRectIds,
            selectedPolygonIds,
            selectedNodeIds,
            selectedEllipseIds,
            doc.lines,
            doc.arcs,
            doc.rects,
            doc.nodes,
            doc.ellipses,
            doc.polygons,
        ],
    );

    const handleTwoPointPointerUp = useCallback(
        (
            e: React.PointerEvent,
            kind: "line" | "arc" | "rect",
            shapeId: string,
        ) => {
            const wasDragging =
                kind === "line"
                    ? dragRef.current.lineId != null
                    : kind === "arc"
                      ? dragRef.current.arcId != null
                      : dragRef.current.rectId != null;
            const moved = dragRef.current.moved;
            const duplicated = dragRef.current.duplicated;
            if (wasDragging) {
                try {
                    (e.target as Element).releasePointerCapture(e.pointerId);
                } catch {
                    /* no-op */
                }
            }
            // A plain click (no movement) selects the shape; shift toggles it
            // in/out of the current selection. After an alt-drag the duplicate is
            // already selected, so leave the selection alone.
            if (!moved && !duplicated && tool === "select") {
                if (kind === "line") {
                    if (e.shiftKey) toggleLineSelection(shapeId);
                    else setSelectedLineId(shapeId);
                } else if (kind === "arc") {
                    if (e.shiftKey) toggleArcSelection(shapeId);
                    else setSelectedArcId(shapeId);
                } else {
                    if (e.shiftKey) toggleRectSelection(shapeId);
                    else setSelectedRectId(shapeId);
                }
            }
            dragRef.current = emptyDragState();
            if (snapGuidesActiveRef.current) setSnapGuides([]);
        },
        [
            tool,
            setSelectedLineId,
            toggleLineSelection,
            setSelectedArcId,
            toggleArcSelection,
            setSelectedRectId,
            toggleRectSelection,
        ],
    );

    // --- Polygon element interactions (select tool) ----------------------
    // Polygons share the grab/drag model of the other standalone shapes, but
    // translate every vertex together (PolygonMover holds the whole point list).

    const handlePolygonPointerDown = useCallback(
        (e: React.PointerEvent, polygon: DiagramPolygon) => {
            // Only the select tool grabs polygons; other tools act on the
            // background (e.g. the polygon tool places vertices).
            if (tool !== "select") return;
            e.stopPropagation();
            const svg = svgRef.current;
            dragRef.current = {
                ...emptyDragState(),
                polygonId: polygon.id,
                downX: e.clientX,
                downY: e.clientY,
                startPoint: svg
                    ? clientToSvgPoint(svg, e.clientX, e.clientY)
                    : null,
            };
            const clonePts = (pts: Vec2[]) => pts.map((p) => ({ ...p }));
            // Alt-drag duplicates: spawn a copy and drag that instead of the
            // original (matching the other shapes). duplicatePolygon pushes its
            // own snapshot, so skip it here.
            if (e.altKey) {
                const newId = duplicatePolygon(polygon.id);
                if (newId) {
                    dragRef.current.polygonId = newId;
                    dragRef.current.duplicated = true;
                    dragRef.current.polygonMovers = [
                        { id: newId, points: clonePts(polygon.points) },
                    ];
                    (e.target as Element).setPointerCapture(e.pointerId);
                    return;
                }
                pushSnapshot();
            } else {
                pushSnapshot();
            }
            // Drag the whole selection together when the grabbed polygon is part
            // of a multi-selection; otherwise just this polygon. A mixed
            // selection drags every shape type as one group.
            const partOfSelection =
                selectedPolygonIds.includes(polygon.id) &&
                (selectedPolygonIds.length > 1 ||
                    selectedNodeIds.length > 0 ||
                    selectedEllipseIds.length > 0 ||
                    selectedLineIds.length > 0 ||
                    selectedArcIds.length > 0 ||
                    selectedRectIds.length > 0);
            const polygons = partOfSelection
                ? doc.polygons.filter((p) =>
                      selectedPolygonIds.includes(p.id),
                  )
                : [polygon];
            dragRef.current.polygonMovers = polygons.map((p) => ({
                id: p.id,
                points: clonePts(p.points),
            }));
            if (partOfSelection) {
                dragRef.current.movers = doc.nodes
                    .filter(
                        (n) =>
                            selectedNodeIds.includes(n.id) && !n.spawnedFrom,
                    )
                    .map((n) => ({ id: n.id, start: { ...n.position } }));
                dragRef.current.ellipseMovers = doc.ellipses
                    .filter((el) => selectedEllipseIds.includes(el.id))
                    .map((el) => ({ id: el.id, start: { ...el.center } }));
                dragRef.current.lineMovers = doc.lines
                    .filter((l) => selectedLineIds.includes(l.id))
                    .map((l) => ({
                        id: l.id,
                        start: { ...l.start },
                        end: { ...l.end },
                    }));
                dragRef.current.arcMovers = doc.arcs
                    .filter((a) => selectedArcIds.includes(a.id))
                    .map((a) => ({
                        id: a.id,
                        start: { ...a.start },
                        end: { ...a.end },
                    }));
                dragRef.current.rectMovers = doc.rects
                    .filter((r) => selectedRectIds.includes(r.id))
                    .map((r) => ({
                        id: r.id,
                        start: { ...r.start },
                        end: { ...r.end },
                    }));
            }
            // Pin every shape mover's symmetric counterparts so the drag mirrors
            // them in lockstep (covers a lone polygon and a mixed selection).
            captureDragShapeMirrors(dragRef.current);
            (e.target as Element).setPointerCapture(e.pointerId);
        },
        [
            tool,
            pushSnapshot,
            duplicatePolygon,
            captureDragShapeMirrors,
            selectedPolygonIds,
            selectedNodeIds,
            selectedEllipseIds,
            selectedLineIds,
            selectedArcIds,
            selectedRectIds,
            doc.polygons,
            doc.nodes,
            doc.ellipses,
            doc.lines,
            doc.arcs,
            doc.rects,
        ],
    );

    const handlePolygonPointerUp = useCallback(
        (e: React.PointerEvent, polygonId: string) => {
            const wasDragging = dragRef.current.polygonId != null;
            const moved = dragRef.current.moved;
            const duplicated = dragRef.current.duplicated;
            if (wasDragging) {
                try {
                    (e.target as Element).releasePointerCapture(e.pointerId);
                } catch {
                    /* no-op */
                }
            }
            // A plain click (no movement) selects the polygon; shift toggles it
            // in/out of the current selection. After an alt-drag the duplicate is
            // already selected, so leave the selection alone.
            if (!moved && !duplicated && tool === "select") {
                if (e.shiftKey) togglePolygonSelection(polygonId);
                else setSelectedPolygonId(polygonId);
            }
            dragRef.current = emptyDragState();
            if (snapGuidesActiveRef.current) setSnapGuides([]);
        },
        [tool, setSelectedPolygonId, togglePolygonSelection],
    );

    // --- Heat zone interactions (select tool) ----------------------------
    // Heat zones use their own lightweight whole-zone drag (a single zone at a
    // time, no mixed-selection group, no in-drag symmetry — symmetry already
    // applies when the zone is created). This keeps the editor-only overlay off
    // the shared multi-shape drag machinery.
    const heatZoneDragRef = useRef<{
        id: string | null;
        downX: number;
        downY: number;
        startPoint: Vec2 | null;
        start: Vec2;
        end: Vec2;
        /**
         * Under active symmetry, the existing mirror-copy zone ids paired by
         * `mirrorPointSets([start, end])` order ("" when none was found). The drag
         * re-mirrors the translated box so each counterpart stays a reflection.
         */
        mirrors: string[];
        moved: boolean;
    }>({
        id: null,
        downX: 0,
        downY: 0,
        startPoint: null,
        start: { x: 0, y: 0 },
        end: { x: 0, y: 0 },
        mirrors: [],
        moved: false,
    });

    const handleHeatZonePointerDown = useCallback(
        (e: React.PointerEvent, zone: DiagramHeatZone) => {
            if (tool !== "select") return;
            e.stopPropagation();
            const svg = svgRef.current;
            pushSnapshot();
            // Capture mirror-copy zones (by corner geometry) so a drag under
            // active symmetry moves each counterpart in lockstep.
            const sym = docRef.current.symmetry;
            const { w, h } = canvasDimsRef.current;
            const c = { x: w / 2, y: h / 2 };
            const claimed = new Set<string>([zone.id]);
            const mirrors = mirrorPointSets(
                [zone.start, zone.end],
                sym,
                c,
            ).map((set) => {
                const match = findShapeNearPoints(
                    docRef.current.heatZones,
                    set.points,
                    (z) => [z.start, z.end],
                    undefined,
                    claimed,
                );
                if (!match) return "";
                claimed.add(match.id);
                return match.id;
            });
            heatZoneDragRef.current = {
                id: zone.id,
                downX: e.clientX,
                downY: e.clientY,
                startPoint: svg
                    ? clientToSvgPoint(svg, e.clientX, e.clientY)
                    : null,
                start: { ...zone.start },
                end: { ...zone.end },
                mirrors,
                moved: false,
            };
            (e.target as Element).setPointerCapture(e.pointerId);
        },
        [tool, pushSnapshot],
    );

    const handleHeatZonePointerMove = useCallback(
        (e: React.PointerEvent) => {
            const drag = heatZoneDragRef.current;
            if (!drag.id || !drag.startPoint) return;
            const svg = svgRef.current;
            if (!svg) return;
            const pt = clientToSvgPoint(svg, e.clientX, e.clientY);
            const dx = pt.x - drag.startPoint.x;
            const dy = pt.y - drag.startPoint.y;
            if (!drag.moved && Math.hypot(dx, dy) < 1) return;
            drag.moved = true;
            const start = { x: drag.start.x + dx, y: drag.start.y + dy };
            const end = { x: drag.end.x + dx, y: drag.end.y + dy };
            const updates: Array<{ id: string; start: Vec2; end: Vec2 }> = [
                { id: drag.id, start, end },
            ];
            if (drag.mirrors.length > 0) {
                const { w, h } = canvasDimsRef.current;
                const c = { x: w / 2, y: h / 2 };
                const sets = mirrorPointSets([start, end], docRef.current.symmetry, c);
                for (let i = 0; i < drag.mirrors.length; i += 1) {
                    const copyId = drag.mirrors[i];
                    const set = sets[i];
                    if (!copyId || !set) continue;
                    updates.push({
                        id: copyId,
                        start: set.points[0]!,
                        end: set.points[1]!,
                    });
                }
            }
            moveHeatZones(updates);
        },
        [moveHeatZones],
    );

    const handleHeatZonePointerUp = useCallback(
        (e: React.PointerEvent, zoneId: string) => {
            const drag = heatZoneDragRef.current;
            const wasDragging = drag.id != null;
            const moved = drag.moved;
            if (wasDragging) {
                try {
                    (e.target as Element).releasePointerCapture(e.pointerId);
                } catch {
                    /* no-op */
                }
            }
            if (!moved && tool === "select") {
                if (e.shiftKey) toggleHeatZoneSelection(zoneId);
                else setSelectedHeatZoneId(zoneId);
            }
            heatZoneDragRef.current = {
                id: null,
                downX: 0,
                downY: 0,
                startPoint: null,
                start: { x: 0, y: 0 },
                end: { x: 0, y: 0 },
                mirrors: [],
                moved: false,
            };
        },
        [tool, setSelectedHeatZoneId, toggleHeatZoneSelection],
    );

    // --- Single-point editing (line/arc/rect/polygon handles) ------------
    // When exactly one line, arc, rect, or polygon is selected (Select tool),
    // small square handles are drawn on each of its defining points. Grabbing a
    // handle drags that one point while the rest of the shape stays put — line/
    // arc endpoints, rect corners (the box stays axis-aligned; the opposite
    // corner pins), and polygon vertices.

    /**
     * Begin a single-point drag from a handle press. Captures the pointer on the
     * handle, snapshots the shape's start geometry, and stops propagation so the
     * background marquee / whole-shape drag don't also start. Pushes one history
     * snapshot here (the live move* updates don't, matching whole-shape drags).
     */
    const handlePointPointerDown = useCallback(
        (
            e: React.PointerEvent,
            kind: ShapePointKind,
            id: string,
            point: PointDragState["point"],
        ) => {
            if (tool !== "select") return;
            if (e.button !== 0) return;
            e.stopPropagation();
            const svg = svgRef.current;
            if (!svg) return;
            const startPoint = clientToSvgPoint(svg, e.clientX, e.clientY);
            const startGeom: PointDragState["startGeom"] = {};
            // Only a rotated rect carries a nonzero rotation; the drag un-rotates
            // the pointer delta into the box's local frame before applying it.
            let rotation = 0;
            if (kind === "polygon") {
                const poly = doc.polygons.find((p) => p.id === id);
                if (!poly) return;
                startGeom.points = poly.points.map((p) => ({ ...p }));
            } else if (kind === "rect") {
                const rect = doc.rects.find((r) => r.id === id);
                if (!rect) return;
                startGeom.start = { ...rect.start };
                startGeom.end = { ...rect.end };
                rotation = rect.rotation;
            } else if (kind === "heat-zone") {
                const zone = doc.heatZones.find((z) => z.id === id);
                if (!zone) return;
                startGeom.start = { ...zone.start };
                startGeom.end = { ...zone.end };
            } else if (kind === "line") {
                const line = doc.lines.find((l) => l.id === id);
                if (!line) return;
                startGeom.start = { ...line.start };
                startGeom.end = { ...line.end };
            } else {
                const arc = doc.arcs.find((a) => a.id === id);
                if (!arc) return;
                startGeom.start = { ...arc.start };
                startGeom.end = { ...arc.end };
            }
            // Under active symmetry, a heat-zone corner drag re-mirrors the box
            // each frame; capture the existing mirror-copy zones now (by corner
            // geometry, paired by `mirrorPointSets` order) so they track as true
            // reflections through the resize.
            let mirrors: string[] | undefined;
            if (
                kind === "heat-zone" &&
                startGeom.start &&
                startGeom.end
            ) {
                const sym = docRef.current.symmetry;
                const { w, h } = canvasDimsRef.current;
                const c = { x: w / 2, y: h / 2 };
                const claimed = new Set<string>([id]);
                mirrors = mirrorPointSets(
                    [startGeom.start, startGeom.end],
                    sym,
                    c,
                ).map((set) => {
                    const match = findShapeNearPoints(
                        docRef.current.heatZones,
                        set.points,
                        (z) => [z.start, z.end],
                        undefined,
                        claimed,
                    );
                    if (!match) return "";
                    claimed.add(match.id);
                    return match.id;
                });
            }
            pushSnapshot();
            pointDragRef.current = {
                kind,
                id,
                point,
                startPoint,
                startGeom,
                rotation,
                mirrors,
                moved: false,
                downX: e.clientX,
                downY: e.clientY,
            };
            (e.target as Element).setPointerCapture(e.pointerId);
        },
        [
            tool,
            pushSnapshot,
            doc.polygons,
            doc.rects,
            doc.lines,
            doc.arcs,
            doc.heatZones,
        ],
    );

    /**
     * Drive an in-progress point drag: recompute the one dragged point from the
     * snapshot geometry + the pointer delta and write it through the matching
     * live-move mutator. Shift locks the delta to H/V/45° (matching whole-shape
     * drags). Rects rebuild from the dragged corner + its pinned opposite so the
     * box stays axis-aligned and any corner can reshape it.
     */
    const handlePointPointerMove = useCallback(
        (e: React.PointerEvent) => {
            const pd = pointDragRef.current;
            if (!pd) return;
            if (
                Math.abs(e.clientX - pd.downX) > MOVE_THRESHOLD ||
                Math.abs(e.clientY - pd.downY) > MOVE_THRESHOLD
            ) {
                pd.moved = true;
            }
            const svg = svgRef.current;
            if (!svg) return;
            const cur = clientToSvgPoint(svg, e.clientX, e.clientY);
            let dx = cur.x - pd.startPoint.x;
            let dy = cur.y - pd.startPoint.y;
            // Shift locks the drag to H/V/45° (matching whole-shape drags).
            if (e.shiftKey) {
                ({ x: dx, y: dy } = constrainDeltaToAxes(dx, dy));
            }

            // The dragged point's start position, so the snapper can align it to
            // nearby static geometry / canvas guides. Computed per shape kind.
            let draggedStart: Vec2 | null = null;
            const start = pd.startGeom.start;
            const end = pd.startGeom.end;
            if (pd.kind === "polygon") {
                const pts = pd.startGeom.points;
                if (pts && typeof pd.point === "number") {
                    draggedStart = pts[pd.point] ?? null;
                }
            } else if (pd.kind === "rect" || pd.kind === "heat-zone") {
                if (start && end) {
                    draggedStart = rectCorners(start, end)[pd.point as RectCorner];
                }
            } else if (start && end) {
                draggedStart = pd.point === "start" ? start : end;
            }
            if (!draggedStart) return;

            // Snapping: align the dragged point to nearby static nodes/shape
            // points and the canvas centerlines / edges. Disabled while shift is
            // held (axis-lock owns the delta then), when the user has it off, and
            // for a rotated rect (its corners move in a local frame, so a
            // world-space snap would fight the un-rotation below).
            if (snapEnabledRef.current && !e.shiftKey && !pd.rotation) {
                const snap = applyPointSnap(
                    pd.kind,
                    pd.id,
                    draggedStart,
                    dx,
                    dy,
                );
                dx = snap.dx;
                dy = snap.dy;
                setSnapGuides(snap.guides);
            } else if (snapGuidesActiveRef.current) {
                setSnapGuides([]);
            }

            // A rotated rect's corner handles are shown spun into place, so the
            // pointer delta is in world space; un-rotate it into the box's local
            // (axis-aligned) frame before reshaping the box below.
            if (pd.kind === "rect" && pd.rotation) {
                const local = rotateAround({ x: dx, y: dy }, { x: 0, y: 0 }, -pd.rotation);
                dx = local.x;
                dy = local.y;
            }

            if (pd.kind === "polygon") {
                const pts = pd.startGeom.points;
                if (!pts || typeof pd.point !== "number") return;
                const next = pts.map((p, i) =>
                    i === pd.point ? { x: p.x + dx, y: p.y + dy } : { ...p },
                );
                movePolygon(pd.id, next);
                return;
            }
            if (!start || !end) return;
            if (pd.kind === "rect") {
                // Drag one corner; rebuild start/end from it and its (pinned)
                // opposite so the box stays axis-aligned. The corners are keyed
                // by on-screen position, so read the pinned one from the start
                // geometry's corners.
                const corner = pd.point as RectCorner;
                const corners = rectCorners(start, end);
                const dragged = {
                    x: corners[corner].x + dx,
                    y: corners[corner].y + dy,
                };
                const pinned = corners[oppositeRectCorner(corner)];
                moveRect(pd.id, pinned, dragged);
                return;
            }
            if (pd.kind === "heat-zone") {
                // Same axis-aligned corner resize as a rect: drag one corner,
                // pin its opposite. Under symmetry, re-mirror the resized box so
                // the counterpart zones captured at press time stay reflections.
                const corner = pd.point as RectCorner;
                const corners = rectCorners(start, end);
                const dragged = {
                    x: corners[corner].x + dx,
                    y: corners[corner].y + dy,
                };
                const pinned = corners[oppositeRectCorner(corner)];
                const updates: Array<{ id: string; start: Vec2; end: Vec2 }> = [
                    { id: pd.id, start: pinned, end: dragged },
                ];
                if (pd.mirrors && pd.mirrors.length > 0) {
                    const { w, h } = canvasDimsRef.current;
                    const c = { x: w / 2, y: h / 2 };
                    const sets = mirrorPointSets(
                        [pinned, dragged],
                        docRef.current.symmetry,
                        c,
                    );
                    for (let i = 0; i < pd.mirrors.length; i += 1) {
                        const copyId = pd.mirrors[i];
                        const set = sets[i];
                        if (!copyId || !set) continue;
                        updates.push({
                            id: copyId,
                            start: set.points[0]!,
                            end: set.points[1]!,
                        });
                    }
                }
                moveHeatZones(updates);
                return;
            }
            // Line / arc: move just the grabbed endpoint.
            const move = pd.kind === "line" ? moveLine : moveArc;
            if (pd.point === "start") {
                move(pd.id, { x: start.x + dx, y: start.y + dy }, end);
            } else {
                move(pd.id, start, { x: end.x + dx, y: end.y + dy });
            }
        },
        [movePolygon, moveRect, moveLine, moveArc, moveHeatZones, applyPointSnap],
    );

    /** End a point drag (release capture, clear the ref + any snap guides). */
    const handlePointPointerUp = useCallback((e: React.PointerEvent) => {
        if (!pointDragRef.current) return;
        try {
            (e.target as Element).releasePointerCapture(e.pointerId);
        } catch {
            /* no-op */
        }
        pointDragRef.current = null;
        if (snapGuidesActiveRef.current) setSnapGuides([]);
    }, []);

    // Whether the current selection can be scaled: a meaningful spread of 2+
    // defining points (node positions, ellipse centers, line/arc endpoints)
    // or a single ellipse, whose radii can still grow. Mirrors the pointer-
    // down `canScale` gate so the hint matches what a drag will do.
    const canScaleSelection = useMemo(() => {
        const nodeCount = doc.nodes.filter(
            (n) => selectedNodeIds.includes(n.id) && !n.spawnedFrom,
        ).length;
        const ellipseCount = doc.ellipses.filter((el) =>
            selectedEllipseIds.includes(el.id),
        ).length;
        const lineCount = doc.lines.filter((l) =>
            selectedLineIds.includes(l.id),
        ).length;
        const arcCount = doc.arcs.filter((a) =>
            selectedArcIds.includes(a.id),
        ).length;
        const rectCount = doc.rects.filter((r) =>
            selectedRectIds.includes(r.id),
        ).length;
        // Polygons contribute all their vertices to the spread.
        const polygonPointCount = doc.polygons
            .filter((p) => selectedPolygonIds.includes(p.id))
            .reduce((sum, p) => sum + p.points.length, 0);
        // Lines/arcs/rects each contribute two endpoints.
        const pointCount =
            nodeCount +
            ellipseCount +
            (lineCount + arcCount + rectCount) * 2 +
            polygonPointCount;
        return pointCount >= 2 || ellipseCount >= 1;
    }, [
        doc.nodes,
        doc.ellipses,
        doc.lines,
        doc.arcs,
        doc.rects,
        doc.polygons,
        selectedNodeIds,
        selectedEllipseIds,
        selectedLineIds,
        selectedArcIds,
        selectedRectIds,
        selectedPolygonIds,
    ]);

    // Whether the current selection can be rotated: 2+ defining points (so an
    // offset exists to spin about the pivot) or a selected partial-arc ellipse
    // whose carve can still turn. Mirrors the pointer-down `canRotate` gate.
    const canRotateSelection = useMemo(() => {
        const nodeCount = doc.nodes.filter(
            (n) => selectedNodeIds.includes(n.id) && !n.spawnedFrom,
        ).length;
        const ellipses = doc.ellipses.filter((el) =>
            selectedEllipseIds.includes(el.id),
        );
        const lineCount = doc.lines.filter((l) =>
            selectedLineIds.includes(l.id),
        ).length;
        const arcCount = doc.arcs.filter((a) =>
            selectedArcIds.includes(a.id),
        ).length;
        const rectCount = doc.rects.filter((r) =>
            selectedRectIds.includes(r.id),
        ).length;
        const polygonPointCount = doc.polygons
            .filter((p) => selectedPolygonIds.includes(p.id))
            .reduce((sum, p) => sum + p.points.length, 0);
        const pointCount =
            nodeCount +
            ellipses.length +
            (lineCount + arcCount + rectCount) * 2 +
            polygonPointCount;
        // A single ellipse is rotatable when it visibly turns: a non-circular
        // body, an existing tilt, or a partial-arc carve (a plain circle isn't).
        const ellipseSpins = ellipses.some(
            (el) =>
                el.startAngle !== 0 ||
                el.rotation !== 0 ||
                Math.abs(el.rx - el.ry) > 1e-6,
        );
        return pointCount >= 2 || ellipseSpins;
    }, [
        doc.nodes,
        doc.ellipses,
        doc.lines,
        doc.arcs,
        doc.rects,
        doc.polygons,
        selectedNodeIds,
        selectedEllipseIds,
        selectedLineIds,
        selectedArcIds,
        selectedRectIds,
        selectedPolygonIds,
    ]);

    // Group connections into chains so bridged presets render as continuous,
    // tangent/corner-matched strokes. Standalone connections come back as
    // length-1 chains, so this also drives ordinary connection rendering.
    // Connections to hidden nodes stay rendered (faint upstream), matching the
    // editor's "hidden but still present" treatment, so no visibility filter.
    const connectionChains = useMemo(() => {
        const nodeById = new Map(doc.nodes.map((n) => [n.id, n]));
        const presetById = new Map(doc.presets.map((p) => [p.id, p]));
        // A connection on a hidden layer is suppressed: its endpoints share that
        // layer, so excluding hidden-layer nodes drops the connection too.
        return bridgedConnectionChains(
            doc,
            nodeById,
            presetById,
            (n) => !hiddenLayerIds.has(n.layerId),
        );
    }, [doc, hiddenLayerIds]);

    /**
     * Connections whose BOTH endpoints are currently selected, with the
     * midpoint of their two node centers. Each gets a hover hit-target plus an
     * "insert node" button (shown on hover) at that midpoint. Only computed in
     * the Select tool so the affordance doesn't intrude on other subtools.
     */
    const insertableConnections = useMemo(() => {
        if (tool !== "select" || selectedNodeIds.length < 2) return [];
        const selected = new Set(selectedNodeIds);
        const posById = new Map(doc.nodes.map((n) => [n.id, n.position]));
        const out: Array<{ id: string; mid: Vec2 }> = [];
        for (const c of doc.connections) {
            if (!selected.has(c.sourceId) || !selected.has(c.targetId)) continue;
            const a = posById.get(c.sourceId);
            const b = posById.get(c.targetId);
            if (!a || !b) continue;
            out.push({
                id: c.id,
                mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
            });
        }
        return out;
    }, [tool, selectedNodeIds, doc.nodes, doc.connections]);

    // Drop the hovered-connection highlight whenever it stops being insertable
    // (selection changed, the connection was split/removed, etc.).
    useEffect(() => {
        if (
            hoveredConnectionId &&
            !insertableConnections.some((c) => c.id === hoveredConnectionId)
        ) {
            setHoveredConnectionId(null);
        }
    }, [hoveredConnectionId, insertableConnections]);

    /**
     * Point-editing handles for the single selected line / arc / rect / polygon.
     * Handles only appear when the Select tool is active and exactly one such
     * shape is selected (and no other shape type), so they never clutter a
     * multi-selection drag. Each handle carries the point id its drag mutates.
     */
    const pointHandles = useMemo((): {
        kind: ShapePointKind;
        id: string;
        handles: Array<{ point: PointDragState["point"]; pos: Vec2 }>;
    } | null => {
        if (tool !== "select") return null;
        const only = (ids: string[], ...others: string[][]) =>
            ids.length === 1 && others.every((o) => o.length === 0);

        if (
            only(
                selectedLineIds,
                selectedArcIds,
                selectedRectIds,
                selectedPolygonIds,
                selectedNodeIds,
                selectedEllipseIds,
                selectedHeatZoneIds,
            )
        ) {
            const line = doc.lines.find((l) => l.id === selectedLineIds[0]);
            if (!line) return null;
            return {
                kind: "line",
                id: line.id,
                handles: [
                    { point: "start", pos: line.start },
                    { point: "end", pos: line.end },
                ],
            };
        }
        if (
            only(
                selectedArcIds,
                selectedLineIds,
                selectedRectIds,
                selectedPolygonIds,
                selectedNodeIds,
                selectedEllipseIds,
                selectedHeatZoneIds,
            )
        ) {
            const arc = doc.arcs.find((a) => a.id === selectedArcIds[0]);
            if (!arc) return null;
            return {
                kind: "arc",
                id: arc.id,
                handles: [
                    { point: "start", pos: arc.start },
                    { point: "end", pos: arc.end },
                ],
            };
        }
        if (
            only(
                selectedRectIds,
                selectedLineIds,
                selectedArcIds,
                selectedPolygonIds,
                selectedNodeIds,
                selectedEllipseIds,
                selectedHeatZoneIds,
            )
        ) {
            const rect = doc.rects.find((r) => r.id === selectedRectIds[0]);
            if (!rect) return null;
            const corners = rectCorners(rect.start, rect.end);
            // Show the handles on the visible (rotated) corners. The drag still
            // edits the axis-aligned box; it un-rotates the pointer delta into
            // the rect's local frame to keep that consistent.
            const c = rectCenter(rect);
            const place = (p: Vec2): Vec2 =>
                rect.rotation ? rotateAround(p, c, rect.rotation) : p;
            return {
                kind: "rect",
                id: rect.id,
                handles: (Object.keys(corners) as RectCorner[]).map((cn) => ({
                    point: cn,
                    pos: place(corners[cn]),
                })),
            };
        }
        if (
            only(
                selectedPolygonIds,
                selectedLineIds,
                selectedArcIds,
                selectedRectIds,
                selectedNodeIds,
                selectedEllipseIds,
                selectedHeatZoneIds,
            )
        ) {
            const poly = doc.polygons.find(
                (p) => p.id === selectedPolygonIds[0],
            );
            if (!poly) return null;
            return {
                kind: "polygon",
                id: poly.id,
                handles: poly.points.map((p, i) => ({ point: i, pos: p })),
            };
        }
        if (
            only(
                selectedHeatZoneIds,
                selectedLineIds,
                selectedArcIds,
                selectedRectIds,
                selectedPolygonIds,
                selectedNodeIds,
                selectedEllipseIds,
            )
        ) {
            const zone = doc.heatZones.find(
                (z) => z.id === selectedHeatZoneIds[0],
            );
            if (!zone) return null;
            if (layerLocked(zone.layerId)) return null;
            const corners = rectCorners(zone.start, zone.end);
            // Same axis-aligned corner handles as a rect (heat zones don't rotate).
            return {
                kind: "heat-zone",
                id: zone.id,
                handles: (Object.keys(corners) as RectCorner[]).map((cn) => ({
                    point: cn,
                    pos: corners[cn],
                })),
            };
        }
        return null;
    }, [
        tool,
        doc.lines,
        doc.arcs,
        doc.rects,
        doc.polygons,
        doc.heatZones,
        selectedLineIds,
        selectedArcIds,
        selectedRectIds,
        selectedPolygonIds,
        selectedNodeIds,
        selectedEllipseIds,
        selectedHeatZoneIds,
        layerLocked,
    ]);

    // --- Per-element renderers ------------------------------------------------
    // Each renders one element's canvas JSX (keyed by its id). The layer loop
    // below drives the painter order: it iterates `doc.layers` back-to-front and,
    // within each layer, renders that layer's elements in the same per-type order
    // the SVG exporter uses (group outlines at the back, node markers on top), so
    // the on-canvas stacking matches the exported SVG. Layer visibility is handled
    // by the loop (hidden layers are skipped), so these don't re-check it; they
    // still read `layerLocked` for per-element grabbability.

    const renderGroup = (group: NodeGroup) => {
        const ownOutline = group.properties.find(
            (p): p is OutlineProperty => p.kind === "outline",
        );
        if (!ownOutline) return null;
        const outline = resolveGroupOutline(
            ownOutline,
            doc.groupOutlinePresets,
        );
        const positions = group.nodeIds
            .map((id) => doc.nodes.find((n) => n.id === id)?.position)
            .filter((p): p is Vec2 => Boolean(p));
        const d = groupOutlinePath(positions, outline);
        if (!d) return null;
        const style = outline.strokeStyleId
            ? doc.strokeStyles.find((s) => s.id === outline.strokeStyleId)
            : undefined;
        return (
            <path
                key={group.id}
                // Cross-highlight from the Groups list: hovering a
                // group's row outlines it here (the outline path
                // isn't a pointer target itself, so this is a
                // one-way panel→canvas highlight).
                className={
                    hoveredId === group.id ? "diag-hovered" : undefined
                }
                d={d}
                fill={resolveFill(outline.fill)}
                stroke={style?.color ?? "#a0c4ff"}
                strokeWidth={style?.strokeWidth ?? 1.5}
                strokeDasharray={style?.dash || undefined}
                strokeLinejoin="round"
            />
        );
    };

    const renderRect = (rect: DiagramRect) => {
        const locked = layerLocked(rect.layerId);
        const d = rectPath(rect);
        if (!d) return null;
        const selected = selectedRectIds.includes(rect.id);
        const c = rectCenter(rect);
        const style = rect.strokeStyleId
            ? doc.strokeStyles.find((s) => s.id === rect.strokeStyleId)
            : undefined;
        return (
            <g
                key={rect.id}
                data-rect-id={rect.id}
                className={`diag-hoverable${hoveredId === rect.id ? " diag-hovered" : ""}`}
                {...hoverProps(rect.id)}
            >
                <path
                    d={d}
                    transform={
                        rect.rotation
                            ? `rotate(${rect.rotation} ${c.x} ${c.y})`
                            : undefined
                    }
                    // With a stroke style the visible look is
                    // the overlay below; this path is the
                    // grabbable hit area (transparent stroke,
                    // but still showing the selection highlight).
                    fill={style ? "transparent" : resolveFill(rect.fill)}
                    stroke={
                        selected
                            ? SELECTED_COLOR
                            : style
                              ? "transparent"
                              : rect.color
                    }
                    strokeWidth={rect.strokeWidth}
                    strokeDasharray={
                        style && !selected
                            ? undefined
                            : rect.dash || undefined
                    }
                    strokeLinejoin="round"
                    style={{
                        cursor: tool === "select" ? "grab" : "default",
                        opacity: rect.visible ? 1 : 0.3,
                        // Make the whole outline (and interior, when
                        // filled) grabbable rather than just the line.
                        // Locked layers aren't grabbable.
                        pointerEvents:
                            tool === "select" && !locked ? "all" : "none",
                    }}
                    onPointerDown={(e) =>
                        handleTwoPointPointerDown(e, "rect", rect)
                    }
                    onPointerMove={handleGrabPointerMove}
                    onPointerUp={(e) =>
                        handleTwoPointPointerUp(e, "rect", rect.id)
                    }
                />
                {style && (
                    <PresetStrokeShape
                        outline={rectOutline(rect)}
                        style={style}
                        fill={resolveFill(rect.fill)}
                        seedKey={rect.id}
                        opacity={rect.visible ? 1 : 0.3}
                        heatZones={doc.heatZones}
                    />
                )}
            </g>
        );
    };

    const renderHeatZone = (zone: DiagramHeatZone) => {
        if (!zone.visible) return null;
        const locked = layerLocked(zone.layerId);
        const x = Math.min(zone.start.x, zone.end.x);
        const y = Math.min(zone.start.y, zone.end.y);
        const w = Math.abs(zone.end.x - zone.start.x);
        const h = Math.abs(zone.end.y - zone.start.y);
        const r = Math.max(0, zone.radius);
        const selected = selectedHeatZoneIds.includes(zone.id);
        return (
            <g
                key={zone.id}
                data-heat-zone-id={zone.id}
                className={`diag-hoverable${hoveredId === zone.id ? " diag-hovered" : ""}`}
                {...hoverProps(zone.id)}
            >
                {/* Influence falloff bound (informational). */}
                {r > 0 && (
                    <rect
                        x={x - r}
                        y={y - r}
                        width={w + 2 * r}
                        height={h + 2 * r}
                        rx={r}
                        ry={r}
                        fill={`${zone.color}14`}
                        stroke={zone.color}
                        strokeOpacity={0.35}
                        strokeWidth={1}
                        strokeDasharray="2 4"
                        style={{ pointerEvents: "none" }}
                    />
                )}
                <rect
                    x={x}
                    y={y}
                    width={w}
                    height={h}
                    fill={`${zone.color}33`}
                    stroke={selected ? SELECTED_COLOR : zone.color}
                    strokeWidth={2}
                    strokeDasharray="6 3"
                    style={{
                        cursor: tool === "select" ? "grab" : "default",
                        pointerEvents:
                            tool === "select" && !locked ? "all" : "none",
                    }}
                    onPointerDown={(e) => handleHeatZonePointerDown(e, zone)}
                    onPointerMove={handleHeatZonePointerMove}
                    onPointerUp={(e) => handleHeatZonePointerUp(e, zone.id)}
                />
            </g>
        );
    };

    const renderPolygon = (polygon: DiagramPolygon) => {
        const locked = layerLocked(polygon.layerId);
        const d = polygonPath(polygon);
        if (!d) return null;
        const selected = selectedPolygonIds.includes(polygon.id);
        const style = polygon.strokeStyleId
            ? doc.strokeStyles.find((s) => s.id === polygon.strokeStyleId)
            : undefined;
        return (
            <g
                key={polygon.id}
                data-polygon-id={polygon.id}
                className={`diag-hoverable${hoveredId === polygon.id ? " diag-hovered" : ""}`}
                {...hoverProps(polygon.id)}
            >
                <path
                    d={d}
                    fill={style ? "transparent" : resolveFill(polygon.fill)}
                    stroke={
                        selected
                            ? SELECTED_COLOR
                            : style
                              ? "transparent"
                              : polygon.color
                    }
                    strokeWidth={polygon.strokeWidth}
                    strokeDasharray={
                        style && !selected
                            ? undefined
                            : polygon.dash || undefined
                    }
                    strokeLinejoin="round"
                    style={{
                        cursor: tool === "select" ? "grab" : "default",
                        opacity: polygon.visible ? 1 : 0.3,
                        // Make the whole outline (and interior, when
                        // filled) grabbable rather than just the line.
                        // Locked layers aren't grabbable.
                        pointerEvents:
                            tool === "select" && !locked ? "all" : "none",
                    }}
                    onPointerDown={(e) =>
                        handlePolygonPointerDown(e, polygon)
                    }
                    onPointerMove={handleGrabPointerMove}
                    onPointerUp={(e) =>
                        handlePolygonPointerUp(e, polygon.id)
                    }
                />
                {style && (
                    <PresetStrokeShape
                        outline={polygonOutline(polygon)}
                        style={style}
                        fill={resolveFill(polygon.fill)}
                        seedKey={polygon.id}
                        opacity={polygon.visible ? 1 : 0.3}
                        heatZones={doc.heatZones}
                    />
                )}
            </g>
        );
    };

    const renderEllipse = (ellipse: DiagramEllipse) => {
        const locked = layerLocked(ellipse.layerId);
        const d = ellipsePath(ellipse);
        if (!d) return null;
        const selected = selectedEllipseIds.includes(ellipse.id);
        const style = ellipse.strokeStyleId
            ? doc.strokeStyles.find((s) => s.id === ellipse.strokeStyleId)
            : undefined;
        return (
            <g
                key={ellipse.id}
                data-ellipse-id={ellipse.id}
                className={`diag-hoverable${hoveredId === ellipse.id ? " diag-hovered" : ""}`}
                {...hoverProps(ellipse.id)}
            >
                <path
                    d={d}
                    transform={
                        ellipse.rotation
                            ? `rotate(${ellipse.rotation} ${ellipse.center.x} ${ellipse.center.y})`
                            : undefined
                    }
                    fill={style ? "transparent" : resolveFill(ellipse.fill)}
                    stroke={
                        selected
                            ? SELECTED_COLOR
                            : style
                              ? "transparent"
                              : ellipse.color
                    }
                    strokeWidth={ellipse.strokeWidth}
                    strokeDasharray={
                        style && !selected
                            ? undefined
                            : ellipse.dash || undefined
                    }
                    strokeLinecap="round"
                    style={{
                        cursor: tool === "select" ? "grab" : "default",
                        opacity: ellipse.visible ? 1 : 0.3,
                        // A stroke-only ellipse has a hollow center;
                        // make the whole outline (and interior, when
                        // filled) grabbable rather than just the line.
                        // Locked layers aren't grabbable.
                        pointerEvents:
                            tool === "select" && !locked ? "all" : "none",
                    }}
                    onPointerDown={(e) =>
                        handleEllipsePointerDown(e, ellipse)
                    }
                    onPointerMove={handleGrabPointerMove}
                    onPointerUp={(e) => handleEllipsePointerUp(e, ellipse)}
                />
                {style && (
                    <PresetStrokeShape
                        outline={ellipseOutline(ellipse)}
                        style={style}
                        fill={resolveFill(ellipse.fill)}
                        seedKey={ellipse.id}
                        opacity={ellipse.visible ? 1 : 0.3}
                        heatZones={doc.heatZones}
                    />
                )}
            </g>
        );
    };

    const renderLine = (line: DiagramLine) => {
        const locked = layerLocked(line.layerId);
        const d = linePath(line);
        if (!d) return null;
        const selected = selectedLineIds.includes(line.id);
        const grabbable = tool === "select" && !locked;
        const style = line.strokeStyleId
            ? doc.strokeStyles.find((s) => s.id === line.strokeStyleId)
            : undefined;
        return (
            <g key={line.id} data-line-id={line.id} className="diag-hoverable">
                {/* Wide transparent hit area so the thin stroke
                    is easy to grab. */}
                {grabbable && (
                    <path
                        d={d}
                        fill="none"
                        stroke="transparent"
                        strokeWidth={Math.max(line.strokeWidth + 8, 12)}
                        strokeLinecap="round"
                        style={{ cursor: "grab" }}
                        onPointerDown={(e) =>
                            handleTwoPointPointerDown(e, "line", line)
                        }
                        onPointerMove={handleGrabPointerMove}
                        onPointerUp={(e) =>
                            handleTwoPointPointerUp(e, "line", line.id)
                        }
                    />
                )}
                {style ? (
                    <PresetStrokeShape
                        outline={lineOutline(line)}
                        style={style}
                        fill="none"
                        seedKey={line.id}
                        opacity={line.visible ? 1 : 0.3}
                        heatZones={doc.heatZones}
                    />
                ) : null}
                {/* The plain stroke (or, with a style, just the
                    selection highlight overlaid on the style). */}
                {(!style || selected) && (
                    <path
                        d={d}
                        fill="none"
                        stroke={selected ? SELECTED_COLOR : line.color}
                        strokeWidth={line.strokeWidth}
                        strokeDasharray={
                            style ? undefined : line.dash || undefined
                        }
                        strokeLinecap="round"
                        style={{
                            opacity: line.visible ? 1 : 0.3,
                            pointerEvents: "none",
                        }}
                    />
                )}
            </g>
        );
    };

    const renderArc = (arc: DiagramArc) => {
        const locked = layerLocked(arc.layerId);
        const d = diagramArcPath(arc);
        if (!d) return null;
        const selected = selectedArcIds.includes(arc.id);
        const grabbable = tool === "select" && !locked;
        const style = arc.strokeStyleId
            ? doc.strokeStyles.find((s) => s.id === arc.strokeStyleId)
            : undefined;
        return (
            <g key={arc.id} data-arc-id={arc.id} className="diag-hoverable">
                {grabbable && (
                    <path
                        d={d}
                        fill="none"
                        stroke="transparent"
                        strokeWidth={Math.max(arc.strokeWidth + 8, 12)}
                        strokeLinecap="round"
                        style={{ cursor: "grab" }}
                        onPointerDown={(e) =>
                            handleTwoPointPointerDown(e, "arc", arc)
                        }
                        onPointerMove={handleGrabPointerMove}
                        onPointerUp={(e) =>
                            handleTwoPointPointerUp(e, "arc", arc.id)
                        }
                    />
                )}
                {style ? (
                    <PresetStrokeShape
                        outline={arcOutline(arc)}
                        style={style}
                        fill="none"
                        seedKey={arc.id}
                        opacity={arc.visible ? 1 : 0.3}
                        heatZones={doc.heatZones}
                    />
                ) : null}
                {(!style || selected) && (
                    <path
                        d={d}
                        fill="none"
                        stroke={selected ? SELECTED_COLOR : arc.color}
                        strokeWidth={arc.strokeWidth}
                        strokeDasharray={
                            style ? undefined : arc.dash || undefined
                        }
                        strokeLinecap="round"
                        style={{
                            opacity: arc.visible ? 1 : 0.3,
                            pointerEvents: "none",
                        }}
                    />
                )}
            </g>
        );
    };

    const renderConnectionChain = (
        chain: (typeof connectionChains)[number],
    ): React.ReactNode[] => {
        const preset = chain.preset;
        // A bridged chain joins several connections into one
        // continuous stroke; standalone connections are length-1
        // chains, so this also covers the unbridged case.
        const key = chain.connectionIds.join("~");
        const kind = preset.renderKind ?? "stroke";
        const objectsAvailable =
            (preset.objectSvgs?.length ?? 0) > 0 &&
            (preset.objectSpacing ?? 0) > 0;
        // Objects-only with no usable objects still strokes.
        const drawStroke =
            kind === "stroke" || kind === "both" || !objectsAvailable;
        const els: React.ReactNode[] = [];
        if (drawStroke) {
            chainConnectionPaths(chain).forEach((d, i) => {
                els.push(
                    <path
                        key={`${key}-${i}`}
                        d={d}
                        fill="none"
                        stroke={preset.color ?? "#a0c4ff"}
                        strokeWidth={preset.strokeWidth ?? 1.5}
                        strokeDasharray={preset.dash || undefined}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />,
                );
            });
        }
        if ((kind === "objects" || kind === "both") && objectsAvailable) {
            const parsed = preset.objectSvgs.map((o) =>
                parseConnectionObject(o.svg),
            );
            connectionObjectPlacements(chain).forEach((p, i) => {
                const obj = parsed[p.objectIndex];
                if (!obj) return;
                els.push(
                    <g
                        key={`${key}-obj-${i}`}
                        transform={connectionObjectTransform(p, obj, preset)}
                        dangerouslySetInnerHTML={{
                            __html: recolorObjectForHeat(
                                obj.inner,
                                preset,
                                { x: p.x, y: p.y },
                                doc.heatZones,
                            ),
                        }}
                    />,
                );
            });
        }
        return els;
    };

    const renderNodeEffects = (node: DiagramNode): React.ReactNode[] =>
        node.effectIds.flatMap((effectId) => {
            const effect = doc.effects.find((e) => e.id === effectId);
            if (!effect) return [];
            const style = effect.strokeStyleId
                ? doc.strokeStyles.find((s) => s.id === effect.strokeStyleId)
                : undefined;
            const stroke = style?.color ?? "#a0c4ff";
            const strokeWidth = style?.strokeWidth ?? 1.5;
            const strokeDasharray = style?.dash || undefined;
            if (effect.kind === "radial-emitter") {
                return radialEmitterSegments(node.position, effect).map(
                    (seg, si) => (
                        <line
                            key={`${node.id}-${effect.id}-${si}`}
                            x1={seg.from.x}
                            y1={seg.from.y}
                            x2={seg.to.x}
                            y2={seg.to.y}
                            stroke={stroke}
                            strokeWidth={strokeWidth}
                            strokeDasharray={strokeDasharray}
                            strokeLinecap="round"
                        />
                    ),
                );
            }
            if (effect.kind === "concentric-rings") {
                return concentricRingRadii(effect).map((r, ri) => (
                    <circle
                        key={`${node.id}-${effect.id}-${ri}`}
                        cx={node.position.x}
                        cy={node.position.y}
                        r={r}
                        fill="none"
                        stroke={stroke}
                        strokeWidth={strokeWidth}
                        strokeDasharray={strokeDasharray}
                    />
                ));
            }
            return [];
        });

    const renderNode = (node: DiagramNode) => {
        const locked = layerLocked(node.layerId);
        let fill = node.heatReactive
            ? applyHeatColor(node.color, node.position, doc.heatZones)
            : node.color;
        if (node.id === connectSourceId) fill = CONNECT_SOURCE_COLOR;
        else if (selectedNodeIds.includes(node.id)) fill = SELECTED_COLOR;
        const geom = nodeShapeGeometry(node.position, node.radius, node.shape);
        const selected = selectedNodeIds.includes(node.id);
        // Spawn clones are derived from a host pattern and can't
        // be grabbed; every other node drags freely. Locked-layer
        // nodes also can't be grabbed.
        const isSpawnClone = Boolean(node.spawnedFrom);
        const grabbable = !isSpawnClone && !locked;
        // Hidden nodes stay visible (faint) in the editor so they
        // can still be selected, moved, and toggled back on.
        const hovered = hoveredId === node.id;
        // `diag-hoverable` gates the pointer `:hover` outline to
        // grabbable nodes (spawn clones / locked layers don't get
        // it — it would imply interactivity). `diag-hovered` is
        // the cross-highlight from the node's panel row and
        // applies to any node, grabbable or not.
        const handlers = {
            "data-node-id": node.id,
            className:
                `${grabbable ? "diag-hoverable" : ""}${
                    hovered ? " diag-hovered" : ""
                }`.trim() || undefined,
            ...hoverProps(node.id),
            style: {
                cursor: grabbable ? "grab" : "default",
                opacity: node.visible ? 1 : 0.3,
                pointerEvents: locked ? ("none" as const) : undefined,
            } as React.CSSProperties,
            onPointerDown: (e: React.PointerEvent) =>
                handleNodePointerDown(e, node),
            onPointerMove: handleGrabPointerMove,
            onPointerUp: (e: React.PointerEvent) =>
                handleNodePointerUp(e, node),
            onDoubleClick: (e: React.MouseEvent) =>
                handleNodeDoubleClick(e, node),
        };
        const stroke = selected ? "#1e1e2e" : undefined;
        const strokeWidth = selected ? 1.5 : undefined;
        const dash = node.visible ? undefined : "3 2";
        if (geom.kind === "rect") {
            return (
                <rect
                    key={node.id}
                    x={geom.x}
                    y={geom.y}
                    width={geom.width}
                    height={geom.height}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeDasharray={dash}
                    {...handlers}
                />
            );
        }
        if (geom.kind === "polygon") {
            return (
                <polygon
                    key={node.id}
                    points={geom.points
                        .map(([x, y]) => `${x},${y}`)
                        .join(" ")}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeDasharray={dash}
                    {...handlers}
                />
            );
        }
        return (
            <circle
                key={node.id}
                cx={geom.cx}
                cy={geom.cy}
                r={geom.r}
                fill={fill}
                stroke={stroke}
                strokeWidth={strokeWidth}
                strokeDasharray={dash}
                {...handlers}
            />
        );
    };

    /**
     * The diagram elements, painted by layer. Layers stack back-to-front in
     * `doc.layers` order (the SVG painter order = the layer list order); hidden
     * layers are skipped. Within each layer the element types keep the order the
     * SVG exporter uses (group outlines → rects → heat zones → polygons →
     * ellipses → lines → arcs → connections → node effects → node markers), so
     * the on-canvas stacking matches the exported SVG. Connections, node effects,
     * and node markers are placed on a node/connection's own `layerId`.
     */
    const layeredElements = useMemo(() => {
        const chainsByLayer = new Map<
            string,
            (typeof connectionChains)[number][]
        >();
        for (const chain of connectionChains) {
            // Every connection in a chain shares its endpoints' layer, so the
            // first connection's layer governs the whole chain.
            const layerId = doc.connections.find(
                (c) => c.id === chain.connectionIds[0],
            )?.layerId;
            if (!layerId) continue;
            const list = chainsByLayer.get(layerId);
            if (list) list.push(chain);
            else chainsByLayer.set(layerId, [chain]);
        }
        return doc.layers.map((layer) => {
            if (!layer.visible) return null;
            const on = (layerId: string) => layerId === layer.id;
            const groups = doc.groups.filter((g) => on(g.layerId));
            const rects = doc.rects.filter((r) => on(r.layerId));
            const heatZones = doc.heatZones.filter((z) => on(z.layerId));
            const polygons = doc.polygons.filter((p) => on(p.layerId));
            const ellipses = doc.ellipses.filter((e) => on(e.layerId));
            const lines = doc.lines.filter((l) => on(l.layerId));
            const arcs = doc.arcs.filter((a) => on(a.layerId));
            const chains = chainsByLayer.get(layer.id) ?? [];
            const nodes = doc.nodes.filter((n) => on(n.layerId));
            return (
                <g key={layer.id} data-layer-id={layer.id}>
                    {groups.map(renderGroup)}
                    {rects.map(renderRect)}
                    {heatZones.map(renderHeatZone)}
                    {polygons.map(renderPolygon)}
                    {ellipses.map(renderEllipse)}
                    {lines.map(renderLine)}
                    {arcs.map(renderArc)}
                    {chains.flatMap(renderConnectionChain)}
                    {nodes.flatMap(renderNodeEffects)}
                    {nodes.map(renderNode)}
                </g>
            );
        });
        // The renderers close over selection/hover/tool/handler state; recompute
        // whenever the document, derived chains, or any of that changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        doc,
        connectionChains,
        tool,
        hoveredId,
        selectedNodeIds,
        selectedEllipseIds,
        selectedLineIds,
        selectedArcIds,
        selectedRectIds,
        selectedPolygonIds,
        selectedHeatZoneIds,
        connectSourceId,
        layerLocked,
        resolveFill,
    ]);

    const hint = useMemo(() => {
        switch (tool) {
            case "place-node":
                return "Click the canvas to place a node.";
            case "connect":
                return connectSourceId
                    ? "Click a second node to connect."
                    : "Click a node to start a connection.";
            case "scale":
                return canScaleSelection
                    ? "Drag to scale: horizontal travel stretches X, vertical stretches Y. Hold Shift to lock to 45° — horizontal/vertical scale one axis, diagonal scales uniformly."
                    : "Select 2+ elements (or an ellipse) with the Select tool to scale them.";
            case "rotate":
                return canRotateSelection
                    ? "Drag around the center to rotate the selection. Hold Shift to snap to 15° steps."
                    : "Select 2+ elements with the Select tool to rotate them about their center.";
            case "ellipse":
                return "Drag on the canvas to draw an ellipse. Hold Shift to lock it to a circle.";
            case "line":
                return "Drag on the canvas to draw a line. Hold Shift to lock its angle.";
            case "arc":
                return "Drag on the canvas to draw an arc. Hold Shift to lock the chord angle; tune the bulge in the panel.";
            case "rect":
                return "Drag on the canvas to draw a rectangle. Hold Shift to lock it to a square.";
            case "polygon":
                return polygonDraft.length === 0
                    ? "Click the canvas to start a polygon, placing one vertex per click."
                    : "Keep clicking to add vertices. Click the first vertex (or press Enter / double-click) to close; Backspace removes the last; Esc cancels.";
            default:
                return canExport
                    ? "Click to select (shift adds), drag a node to move it, or drag empty space to rectangle-select."
                    : "Switch to Place Node and click the canvas.";
        }
    }, [
        tool,
        connectSourceId,
        canExport,
        canScaleSelection,
        canRotateSelection,
        polygonDraft.length,
    ]);

    return (
        <div className="panel-viewer svg-diagram-viewer">
            <ToolRail
                ariaLabel="Diagram subtools"
                items={SUBTOOL_ITEMS}
                activeId={tool}
                onSelect={setTool}
            />
            <div className="path-tool-preview">
                <svg
                    ref={svgRef}
                    viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
                    onPointerDown={handleBgPointerDown}
                    onPointerMove={handleBgPointerMove}
                    onPointerUp={handleBgPointerUp}
                    onPointerLeave={() => {
                        if (interactCursor) setInteractCursor(null);
                    }}
                    onDoubleClick={handleBgDoubleClick}
                    onContextMenu={handleContextMenu}
                    style={{
                        width: "100%",
                        flex: 1,
                        minHeight: 380,
                        background,
                        cursor:
                            tool === "place-node" ||
                            tool === "node-brush" ||
                            tool === "multi-node-brush" ||
                            tool === "mega-node-brush" ||
                            tool === "ellipse" ||
                            tool === "line" ||
                            tool === "arc" ||
                            tool === "rect" ||
                            tool === "polygon" ||
                            tool === "group"
                                ? "crosshair"
                                : tool === "scale"
                                  ? "nwse-resize"
                                  : tool === "rotate"
                                    ? "grab"
                                    : "default",
                        touchAction: "none",
                    }}
                >
                    {/* Hover highlight: tracing a crisp accent outline around a
                        node or shape. Done with an SVG filter applied via CSS so
                        a pointer-driven hover never triggers a React re-render.
                        The filter dilates the element's alpha to a slightly
                        larger silhouette, floods that with the accent color, and
                        composites the original back on top — yielding a hard
                        outline ring (not a soft glow) around any shape, including
                        stroke-only lines/arcs.

                        Two triggers share the filter: `.diag-hoverable:hover`
                        (the pointer over the canvas element, only under the
                        Select tool so it doesn't distract while drawing) and
                        `.diag-hovered` (the element whose panel row is hovered —
                        cross-highlight from the control list, active under any
                        tool). The <defs> is always present so the latter resolves
                        regardless of the current tool. */}
                    <defs>
                        <filter
                            id="diag-hover-outline"
                            x="-30%"
                            y="-30%"
                            width="160%"
                            height="160%"
                        >
                            {/* Grow the source alpha outward by ~2px to
                                form the outline's outer edge. */}
                            <feMorphology
                                in="SourceAlpha"
                                operator="dilate"
                                radius="2"
                                result="dilated"
                            />
                            {/* Paint the dilated silhouette the accent
                                color (kept only where the dilation is). */}
                            <feFlood floodColor={SELECTED_COLOR} />
                            <feComposite
                                in2="dilated"
                                operator="in"
                                result="outline"
                            />
                            {/* Lay the original element back over its
                                outline so only the ring shows around it. */}
                            <feMerge>
                                <feMergeNode in="outline" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>
                    <style>{`
                        .diag-hovered {
                            filter: url(#diag-hover-outline);
                        }
                        ${
                            tool === "select"
                                ? ".diag-hoverable:hover { filter: url(#diag-hover-outline); }"
                                : ""
                        }
                    `}</style>

                    {/* Tiled-SVG fill patterns: the same <pattern> defs the
                        exporter emits, so url(#pattern-…) fills resolve on the
                        live canvas. Injected as markup (the parsed object SVGs
                        are arbitrary inner SVG, not React elements). */}
                    {patternDefsMarkup && (
                        <defs
                            dangerouslySetInnerHTML={{
                                __html: patternDefsMarkup,
                            }}
                        />
                    )}

                    {/* Reference image: a tracing aid drawn beneath everything,
                        letterboxed into the canvas (preserving aspect ratio) and
                        non-interactive so it never intercepts pointer events.
                        Canvas-only — it is excluded from the exported SVG. */}
                    {refImage && refImageVisible && (
                        <image
                            href={refImage.dataUrl}
                            x={0}
                            y={0}
                            width={viewBoxWidth}
                            height={viewBoxHeight}
                            preserveAspectRatio="xMidYMid meet"
                            opacity={refImageOpacity}
                            style={{ pointerEvents: "none" }}
                        />
                    )}

                    <rect
                        x={0}
                        y={0}
                        width={viewBoxWidth}
                        height={viewBoxHeight}
                        fill="none"
                        stroke="#808080"
                        strokeWidth={1}
                        vectorEffect="non-scaling-stroke"
                        style={{ pointerEvents: "none" }}
                    />

                    {layeredElements}

                    {/* Insert-node affordance: for each connection whose BOTH
                        endpoints are selected, an invisible thick hit-target
                        along the chord reveals a "+" button at the midpoint on
                        hover. Clicking it inserts a node there and reroutes the
                        connection through it. Sized in canvas units from the
                        current zoom so the button stays a constant on-screen
                        size. Drawn above nodes so the button is always clickable. */}
                    {insertableConnections.length > 0 &&
                        (() => {
                            const zoom = viewBoxWidth / view.w;
                            const r = 9 / zoom; // ~18px button on screen
                            const hit = 12 / zoom; // hover target half-thickness
                            const tick = 5 / zoom; // "+" arm length
                            return insertableConnections.map((c) => {
                                const hovered = hoveredConnectionId === c.id;
                                return (
                                    <g key={`insert-${c.id}`}>
                                        {/* Invisible hover hit-target centered
                                            on the midpoint (a generous box so
                                            the button is easy to summon). */}
                                        <rect
                                            x={c.mid.x - hit * 2}
                                            y={c.mid.y - hit * 2}
                                            width={hit * 4}
                                            height={hit * 4}
                                            fill="transparent"
                                            onPointerEnter={() =>
                                                setHoveredConnectionId(c.id)
                                            }
                                            onPointerLeave={() =>
                                                setHoveredConnectionId((cur) =>
                                                    cur === c.id ? null : cur,
                                                )
                                            }
                                            style={{ cursor: "pointer" }}
                                        />
                                        {hovered && (
                                            <g
                                                onPointerEnter={() =>
                                                    setHoveredConnectionId(c.id)
                                                }
                                                onPointerDown={(e) => {
                                                    e.stopPropagation();
                                                }}
                                                onPointerUp={(e) => {
                                                    e.stopPropagation();
                                                    insertNodeOnConnection(c.id);
                                                    setHoveredConnectionId(null);
                                                }}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <circle
                                                    cx={c.mid.x}
                                                    cy={c.mid.y}
                                                    r={r}
                                                    fill="#1e1e2e"
                                                    stroke={SELECTED_COLOR}
                                                    strokeWidth={1.5}
                                                    vectorEffect="non-scaling-stroke"
                                                />
                                                <line
                                                    x1={c.mid.x - tick}
                                                    y1={c.mid.y}
                                                    x2={c.mid.x + tick}
                                                    y2={c.mid.y}
                                                    stroke="#ffffff"
                                                    strokeWidth={1.5}
                                                    strokeLinecap="round"
                                                    vectorEffect="non-scaling-stroke"
                                                    style={{
                                                        pointerEvents: "none",
                                                    }}
                                                />
                                                <line
                                                    x1={c.mid.x}
                                                    y1={c.mid.y - tick}
                                                    x2={c.mid.x}
                                                    y2={c.mid.y + tick}
                                                    stroke="#ffffff"
                                                    strokeWidth={1.5}
                                                    strokeLinecap="round"
                                                    vectorEffect="non-scaling-stroke"
                                                    style={{
                                                        pointerEvents: "none",
                                                    }}
                                                />
                                            </g>
                                        )}
                                    </g>
                                );
                            });
                        })()}

                    {/* Point-editing handles: a draggable square per defining
                        point of the single selected line/arc/rect/polygon.
                        Sized in canvas units from the current zoom so they stay
                        a constant on-screen size. Drawn last among interactive
                        layers so they sit on top and are easy to grab. */}
                    {pointHandles &&
                        (() => {
                            const zoom = viewBoxWidth / view.w;
                            const half = 4 / zoom; // ~8px square on screen
                            return (
                                <g>
                                    {pointHandles.handles.map((h) => (
                                        <rect
                                            key={`pt-${pointHandles.id}-${String(
                                                h.point,
                                            )}`}
                                            x={h.pos.x - half}
                                            y={h.pos.y - half}
                                            width={half * 2}
                                            height={half * 2}
                                            fill="#ffffff"
                                            stroke={SELECTED_COLOR}
                                            strokeWidth={1.5}
                                            vectorEffect="non-scaling-stroke"
                                            style={{ cursor: "move" }}
                                            onPointerDown={(e) =>
                                                handlePointPointerDown(
                                                    e,
                                                    pointHandles.kind,
                                                    pointHandles.id,
                                                    h.point,
                                                )
                                            }
                                            onPointerMove={handlePointPointerMove}
                                            onPointerUp={handlePointPointerUp}
                                        />
                                    ))}
                                </g>
                            );
                        })()}

                    {marqueeRect && (
                        <rect
                            x={marqueeRect.x}
                            y={marqueeRect.y}
                            width={marqueeRect.w}
                            height={marqueeRect.h}
                            fill="rgba(124, 167, 255, 0.15)"
                            stroke="#7ca7ff"
                            strokeDasharray="4 2"
                            style={{ pointerEvents: "none" }}
                        />
                    )}

                    {ellipseDrawRect &&
                        ellipseDrawRect.w > 0 &&
                        ellipseDrawRect.h > 0 && (
                            <ellipse
                                cx={ellipseDrawRect.x + ellipseDrawRect.w / 2}
                                cy={ellipseDrawRect.y + ellipseDrawRect.h / 2}
                                rx={ellipseDrawRect.w / 2}
                                ry={ellipseDrawRect.h / 2}
                                fill="rgba(124, 167, 255, 0.12)"
                                stroke="#7ca7ff"
                                strokeDasharray="4 2"
                                style={{ pointerEvents: "none" }}
                            />
                        )}

                    {twoPointDrawStartRef.current &&
                        twoPointDrawEnd &&
                        (() => {
                            const start = twoPointDrawStartRef.current!;
                            const d =
                                tool === "arc"
                                    ? diagramArcPath({
                                          start,
                                          end: twoPointDrawEnd,
                                          flip: false,
                                          sweep: 0.5,
                                      })
                                    : tool === "rect" || tool === "heat-zone"
                                      ? rectPath({
                                            start,
                                            end: twoPointDrawEnd,
                                            cornerRadius: 0,
                                            cornerStyle: "round",
                                        })
                                      : linePath({
                                            start,
                                            end: twoPointDrawEnd,
                                        });
                            if (!d) return null;
                            const previewColor =
                                tool === "heat-zone"
                                    ? DEFAULT_HEAT_ZONE_COLOR
                                    : "#7ca7ff";
                            return (
                                <path
                                    d={d}
                                    fill={
                                        tool === "heat-zone"
                                            ? `${previewColor}22`
                                            : "none"
                                    }
                                    stroke={previewColor}
                                    strokeWidth={2}
                                    strokeDasharray="4 2"
                                    strokeLinecap="round"
                                    style={{ pointerEvents: "none" }}
                                />
                            );
                        })()}

                    {/* In-progress polygon: the placed edges (solid), a rubber-
                        band segment to the cursor, a faint closing segment back
                        to the first vertex, and a dot per placed vertex (the
                        first highlighted as the close target). */}
                    {/* Attract/repel/connect influence-radius preview: a ring
                        following the cursor showing which nodes a click would
                        affect. The color hints at the action (warm = attract,
                        cool = repel, green = connect). */}
                    {tool === "interact" &&
                        interactCursor &&
                        nodeInteractOptions.radius > 0 && (
                            <g style={{ pointerEvents: "none" }}>
                                {/* The cursor ring plus, under active symmetry, a
                                    fainter ring at each mirror position — the
                                    pulse mirrors the whole effect, so this shows
                                    where the symmetric pulse will also land. */}
                                {[
                                    { p: interactCursor, mirror: false },
                                    ...mirrorPositions(
                                        interactCursor,
                                        doc.symmetry,
                                        {
                                            x: viewBoxWidth / 2,
                                            y: viewBoxHeight / 2,
                                        },
                                    ).map((p) => ({ p, mirror: true })),
                                ].map(({ p, mirror }, i) => (
                                    <g
                                        key={i}
                                        opacity={mirror ? 0.5 : 1}
                                    >
                                        <circle
                                            cx={p.x}
                                            cy={p.y}
                                            r={nodeInteractOptions.radius}
                                            fill="none"
                                            stroke={INTERACT_PREVIEW_COLOR[
                                                nodeInteractOptions.action
                                            ]}
                                            strokeWidth={1.5}
                                            strokeDasharray="6 4"
                                            vectorEffect="non-scaling-stroke"
                                        />
                                        <circle
                                            cx={p.x}
                                            cy={p.y}
                                            r={2}
                                            fill={INTERACT_PREVIEW_COLOR[
                                                nodeInteractOptions.action
                                            ]}
                                            vectorEffect="non-scaling-stroke"
                                        />
                                    </g>
                                ))}
                            </g>
                        )}

                    {tool === "polygon" && polygonDraft.length > 0 && (
                        <g style={{ pointerEvents: "none" }}>
                            {polygonDraft.length > 1 && (
                                <polyline
                                    points={polygonDraft
                                        .map((p) => `${p.x},${p.y}`)
                                        .join(" ")}
                                    fill="none"
                                    stroke="#7ca7ff"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    vectorEffect="non-scaling-stroke"
                                />
                            )}
                            {polygonCursor && (
                                <line
                                    x1={
                                        polygonDraft[
                                            polygonDraft.length - 1
                                        ]!.x
                                    }
                                    y1={
                                        polygonDraft[
                                            polygonDraft.length - 1
                                        ]!.y
                                    }
                                    x2={polygonCursor.x}
                                    y2={polygonCursor.y}
                                    stroke="#7ca7ff"
                                    strokeWidth={2}
                                    strokeDasharray="4 2"
                                    strokeLinecap="round"
                                    vectorEffect="non-scaling-stroke"
                                />
                            )}
                            {polygonDraft.length >= 2 && (
                                <line
                                    x1={polygonCursor?.x ?? polygonDraft[
                                        polygonDraft.length - 1
                                    ]!.x}
                                    y1={polygonCursor?.y ?? polygonDraft[
                                        polygonDraft.length - 1
                                    ]!.y}
                                    x2={polygonDraft[0]!.x}
                                    y2={polygonDraft[0]!.y}
                                    stroke="#7ca7ff"
                                    strokeWidth={1}
                                    strokeDasharray="2 3"
                                    strokeLinecap="round"
                                    opacity={0.5}
                                    vectorEffect="non-scaling-stroke"
                                />
                            )}
                            {polygonDraft.map((p, i) => (
                                <circle
                                    key={i}
                                    cx={p.x}
                                    cy={p.y}
                                    r={i === 0 ? 5 : 3}
                                    fill={i === 0 ? "#06d6a0" : "#7ca7ff"}
                                    stroke="#1e1e2e"
                                    strokeWidth={1}
                                    vectorEffect="non-scaling-stroke"
                                />
                            ))}
                        </g>
                    )}

                    {scaleAnchor && (
                        <g
                            stroke={SCALE_ANCHOR_COLOR}
                            strokeWidth={1.5}
                            vectorEffect="non-scaling-stroke"
                            style={{ pointerEvents: "none" }}
                        >
                            <circle
                                cx={scaleAnchor.x}
                                cy={scaleAnchor.y}
                                r={6}
                                fill="none"
                                vectorEffect="non-scaling-stroke"
                            />
                            <line
                                x1={scaleAnchor.x - 10}
                                y1={scaleAnchor.y}
                                x2={scaleAnchor.x + 10}
                                y2={scaleAnchor.y}
                                vectorEffect="non-scaling-stroke"
                            />
                            <line
                                x1={scaleAnchor.x}
                                y1={scaleAnchor.y - 10}
                                x2={scaleAnchor.x}
                                y2={scaleAnchor.y + 10}
                                vectorEffect="non-scaling-stroke"
                            />
                        </g>
                    )}

                    {rotateAnchor && (
                        <g
                            stroke={ROTATE_PIVOT_COLOR}
                            strokeWidth={1.5}
                            vectorEffect="non-scaling-stroke"
                            style={{ pointerEvents: "none" }}
                        >
                            <circle
                                cx={rotateAnchor.x}
                                cy={rotateAnchor.y}
                                r={10}
                                fill="none"
                                vectorEffect="non-scaling-stroke"
                            />
                            <circle
                                cx={rotateAnchor.x}
                                cy={rotateAnchor.y}
                                r={1.5}
                                fill={ROTATE_PIVOT_COLOR}
                                vectorEffect="non-scaling-stroke"
                            />
                        </g>
                    )}

                    {doc.symmetry.guideX && (
                        <line
                            x1={viewBoxWidth / 2}
                            y1={0}
                            x2={viewBoxWidth / 2}
                            y2={viewBoxHeight}
                            stroke={CENTER_GUIDE_COLOR}
                            strokeWidth={1}
                            strokeDasharray="6 4"
                            vectorEffect="non-scaling-stroke"
                            style={{ pointerEvents: "none" }}
                        />
                    )}
                    {doc.symmetry.guideY && (
                        <line
                            x1={0}
                            y1={viewBoxHeight / 2}
                            x2={viewBoxWidth}
                            y2={viewBoxHeight / 2}
                            stroke={CENTER_GUIDE_COLOR}
                            strokeWidth={1}
                            strokeDasharray="6 4"
                            vectorEffect="non-scaling-stroke"
                            style={{ pointerEvents: "none" }}
                        />
                    )}

                    {snapGuides.map((g, i) => (
                        <line
                            key={`snap-${g.axis}-${i}`}
                            x1={g.axis === "x" ? g.coord : g.min}
                            y1={g.axis === "x" ? g.min : g.coord}
                            x2={g.axis === "x" ? g.coord : g.max}
                            y2={g.axis === "x" ? g.max : g.coord}
                            stroke={SNAP_GUIDE_COLOR}
                            strokeWidth={1}
                            vectorEffect="non-scaling-stroke"
                            style={{ pointerEvents: "none" }}
                        />
                    ))}
                </svg>

                <div className="path-tool-footer">
                    <p className="path-tool-meta">{hint}</p>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <span
                            className="path-tool-meta"
                            style={{ minWidth: 44, textAlign: "right" }}
                        >
                            {Math.round((viewBoxWidth / view.w) * 100)}%
                        </span>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={resetView}
                            disabled={
                                view.x === 0 &&
                                view.y === 0 &&
                                view.w === viewBoxWidth &&
                                view.h === viewBoxHeight
                            }
                        >
                            Reset View
                        </button>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={handleDownload}
                            disabled={!canExport}
                        >
                            Download SVG
                        </button>
                    </div>
                </div>
                <SvgDiagram2DNodePanel />
                <SvgDiagram2DMultiNodePanel />
                <SvgDiagram2DEllipsePanel />
                <SvgDiagram2DLinePanel />
                <SvgDiagram2DArcPanel />
                <SvgDiagram2DRectPanel />
                <SvgDiagram2DPolygonPanel />
                <SvgDiagram2DMultiSelectPanel />
                <SvgDiagram2DSubtoolPanel />
                {contextMenu && (
                    <SvgDiagram2DContextMenu
                        menu={contextMenu}
                        onClose={closeContextMenu}
                    />
                )}
            </div>
            <ToolRailPopover
                ariaLabel="Diagram settings"
                items={CONFIG_RAIL_ITEMS}
                side="right"
                openRequest={configSectionRevealRequest}
            />
        </div>
    );
}
